import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  TextField,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Autocomplete,
  MenuItem,
  Grid,
  Snackbar,
  Collapse,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  Menu,
  MenuItem as MuiMenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useTeacher } from '../../context/TeacherContext';
import { useLocation, useNavigate } from 'react-router-dom';
import apiAxiosInstance from "../../utils/axiosInstance";
import { useTeacherStandard } from '../../hooks/useTeacherStandard';
import { useFilterStandardsByTeaching } from '../../hooks/useFilterStandardsByTeaching';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SendIcon from '@mui/icons-material/Send';
import ChatIcon from '@mui/icons-material/Chat';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import PrintIcon from '@mui/icons-material/Print';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import VideoHelpButton from '../Common/VideoHelpButton';
import { LessonPlanForm, LessonPlan, LessonSection, initialLessonPlan, TranslationStatus, MATH_DIFFICULTY_LEVELS } from './types/lessonPlan.types';
import { useStreamingGeneration } from './hooks/useStreamingGeneration';
import { openaiService } from './services/openaiService';
import SectionTabs from './components/ContentSections/SectionTabs';
import IntroductionSection from './components/ContentSections/IntroductionSection';
import IndependentPracticeSection from './components/ContentSections/IndependentPracticeSection';
// Translation regenerating state is passed to sections for UI feedback
import html2pdf from 'html2pdf.js';
import { GENRES, Genre } from '../../config/genres';
import { 
  createTranslationMetadata
} from './utils/translationStatus';

interface Standard {
  _id: string;
  code: string;
  standard: string;
  description: string;
  strand?: string;
  gradeLevel?: string;
  short_description?: string;
  teachingStandard?: string;
}

interface LexileLevel {
  level: string;
  description?: string;
}



interface Language {
  value: string;
  label: string;
  description?: string;
}

// Suppress ResizeObserver errors
const suppressResizeObserverError = () => {
  const resizeObserverErrDiv = document.getElementById('webpack-dev-server-client-overlay-div');
  const resizeObserverErr = document.getElementById('webpack-dev-server-client-overlay');
  if (resizeObserverErr) {
    resizeObserverErr.setAttribute('style', 'display: none');
  }
  if (resizeObserverErrDiv) {
    resizeObserverErrDiv.setAttribute('style', 'display: none');
  }
};

/**
 * SmallGroupLessonPlan Component
 * 
 * Main component for creating AI-generated small group lesson plans.
 * Integrates form controls, content generation, and tabbed sections.
 * Supports real-time streaming of content generation from OpenAI.
 */
const SmallGroupLessonPlan: React.FC = () => {
  const { teacher } = useTeacher();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [lessonPlan, setLessonPlan] = useState<LessonPlan>(initialLessonPlan);
  const [activeTab, setActiveTab] = useState<'introduction' | 'independent'>('introduction');
  const [standards, setStandards] = useState<Standard[]>([]);
  const [selectedStandard, setSelectedStandard] = useState<Standard | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // Section-specific Lexile levels - each section can have its own progression
  const [sectionLexileLevels, setSectionLexileLevels] = useState<{
    introduction: string;
    independent: string;
    baseLexileLevel: string; // The original level from the form
  }>({
    introduction: '',
    independent: '',
    baseLexileLevel: ''
  });

  // Section-specific paragraph/stanza counts - each section tracks its own count independently
  const [sectionParagraphCounts, setSectionParagraphCounts] = useState<{
    introduction: number;
    independent: number;
    baseParagraphCount: number; // The original count from the form
  }>({
    introduction: 0,
    independent: 0,
    baseParagraphCount: 0
  });

  // Collapsible parameters state
  const [parametersExpanded, setParametersExpanded] = useState(true);
  
  // Subject selection state - default to teacher's primarySubject, fallback to 'ELA'
  const [selectedSubject, setSelectedSubject] = useState<string>(
    teacher?.primarySubject && ['ELA', 'Math', 'Science', 'Social Studies'].includes(teacher.primarySubject) 
      ? teacher.primarySubject 
      : 'ELA'
  );
  
  // Initialize subject from teacher's primarySubject on mount
  useEffect(() => {
    if (teacher?.primarySubject && ['ELA', 'Math', 'Science', 'Social Studies'].includes(teacher.primarySubject)) {
      if (!selectedSubject || selectedSubject === 'ELA') {
        setSelectedSubject(teacher.primarySubject);
      }
    }
  }, [teacher?.primarySubject]);
  
  // Sync selectedSubject with lessonPlan.form.subject
  useEffect(() => {
    if (lessonPlan.form.subject && lessonPlan.form.subject !== selectedSubject) {
      setSelectedSubject(lessonPlan.form.subject);
    }
  }, [lessonPlan.form.subject]);
  
  // Chat functionality state
  const [chatMessage, setChatMessage] = useState('');
  const [chatHistory, setChatHistory] = useState<Array<{
    id: string;
    message: string;
    timestamp: Date;
    type: 'user' | 'system';
  }>>([]);
  const [isChatProcessing, setIsChatProcessing] = useState(false);
  const [chatAbortController, setChatAbortController] = useState<AbortController | null>(null);
  
  // Backup state for reverting cancelled modifications
  const [contentBackup, setContentBackup] = useState<{
    section: 'introduction' | 'independent';
    story: string | null;
    activities: string | null;
  } | null>(null);
  
  // Backup state for reverting cancelled generation
  const [generationBackup, setGenerationBackup] = useState<{
    introduction: {
      story: string | null;
      activities: string | null;
    };
    independent: {
      story: string | null;
      activities: string | null;
    };
  } | null>(null);
  
  // Streaming content state for real-time display (removed - using section-based streaming instead)
  
  // Download functionality state
  const [downloadAnchorEl, setDownloadAnchorEl] = useState<null | HTMLElement>(null);
  
  // Translation functionality state
  const [selectedTranslateLanguage, setSelectedTranslateLanguage] = useState<string>('spanish');
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationsRegenerating, setTranslationsRegenerating] = useState<{
    introduction: boolean;
    independent: boolean;
  }>({ introduction: false, independent: false });

  // Available target languages for translation, always include English
  const allTranslateOptions = useMemo(() => [
    { value: 'english', label: 'English' },
    { value: 'spanish', label: 'Spanish' },
    { value: 'french', label: 'French' },
    { value: 'arabic', label: 'Arabic' },
    { value: 'vietnamese', label: 'Vietnamese' },
    { value: 'chinese', label: 'Chinese (Simplified)' },
  ], []);

  // Filter out the currently selected source language from translate-to options
  const filteredTranslateOptions = useMemo(() => {
    const sourceLang = lessonPlan.form.language || 'english';
    return allTranslateOptions.filter(opt => opt.value !== sourceLang);
  }, [allTranslateOptions, lessonPlan.form.language]);

  // Ensure selected translate language is valid when source language changes
  useEffect(() => {
    if (!filteredTranslateOptions.find(o => o.value === selectedTranslateLanguage)) {
      // Prefer Spanish if available, otherwise first option
      const fallback = filteredTranslateOptions.find(o => o.value === 'spanish') || filteredTranslateOptions[0];
      if (fallback) setSelectedTranslateLanguage(fallback.value);
    }
  }, [filteredTranslateOptions, selectedTranslateLanguage]);
  
  // Ref for chat history auto-scroll
  const chatHistoryRef = useRef<HTMLDivElement>(null);

  // Confirmation dialog state
  const [confirmNewLessonOpen, setConfirmNewLessonOpen] = useState(false);
  const [confirmGenerateOpen, setConfirmGenerateOpen] = useState(false);

  // Rotating placeholder examples for Content Modifier
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const placeholderExamples = [
    "Summarize this section",
    "Add fun facts about dinosaurs", 
    "Rewrite for grade 3",
    "Make the story more engaging",
    "Add vocabulary practice",
    "Include more comprehension questions",
    "Make it more challenging",
    "Simplify the language"
  ];

  // Lexile levels available for selection
  const lexileLevels: LexileLevel[] = [
    { level: 'BR-200L', description: 'Beginning Reader to 200 Lexile' },
    { level: '201L-400L', description: '201 to 400 Lexile' },
    { level: '401L-600L', description: '401 to 600 Lexile' },
    { level: '601L-800L', description: '601 to 800 Lexile' },
    { level: '801L-1000L', description: '801 to 1000 Lexile' },
    { level: '1001L+', description: '1001+ Lexile' },
  ];

  // Map grade level to recommended reading level
  const getRecommendedLexileLevel = (gradeLevel?: string): string => {
    if (!gradeLevel) return '';
    
    const grade = gradeLevel.toLowerCase();
    
    // K-1: Beginning Reader
    if (grade.includes('k') || grade.includes('kind') || grade === '1' || grade.includes('1st') || grade.includes('first')) {
      return 'BR-200L';
    }
    // 2-3: 201-400
    if (grade === '2' || grade.includes('2nd') || grade.includes('second') || 
        grade === '3' || grade.includes('3rd') || grade.includes('third')) {
      return '201L-400L';
    }
    // 4-5: 401-600
    if (grade === '4' || grade.includes('4th') || grade.includes('fourth') ||
        grade === '5' || grade.includes('5th') || grade.includes('fifth')) {
      return '401L-600L';
    }
    // 6-7: 601-800
    if (grade === '6' || grade.includes('6th') || grade.includes('sixth') ||
        grade === '7' || grade.includes('7th') || grade.includes('seventh')) {
      return '601L-800L';
    }
    // 8-9: 801-1000
    if (grade === '8' || grade.includes('8th') || grade.includes('eighth') ||
        grade === '9' || grade.includes('9th') || grade.includes('ninth')) {
      return '801L-1000L';
    }
    // 10+: 1001+
    if (grade.match(/1[0-2]/) || grade.includes('10th') || grade.includes('11th') || grade.includes('12th') ||
        grade.includes('tenth') || grade.includes('eleventh') || grade.includes('twelfth')) {
      return '1001L+';
    }
    
    return '';
  };

  // Writing genres available for selection (using centralized configuration)
  const genres: Genre[] = GENRES;

  // Content generation language (fixed to English generation; translation handled separately)
  const languages: Language[] = [
    { value: 'english', label: 'English', description: 'Generate content in English' },
    { value: 'spanish', label: 'Spanish', description: 'Generate content in Spanish' }
  ];

  // Grade-aware story font sizing for print/download only (K-2 larger)
  const getNormalizedEarlyGrade = (): 'K' | 1 | 2 | null => {
    const raw = teacher?.gradeLevel ? String(teacher.gradeLevel).toLowerCase() : '';
    if (!raw) return null;
    if (raw.includes('k') || raw.includes('kind')) return 'K';
    const match = raw.match(/\d+/);
    if (match) {
      const num = parseInt(match[0], 10);
      if (num === 0) return 'K';
      if (num === 1) return 1;
      if (num === 2) return 2;
    }
    return null;
  };

  // HTML font size (px) used for PDF and Print story content; null for default (>2)
  const getStoryFontSizePx = (): number | null => {
    const grade = getNormalizedEarlyGrade();
    switch (grade) {
      case 2:
        return 18; // Larger for 2nd
      case 1:
        return 20; // A little larger than 2nd
      case 'K':
        return 22; // Largest for Kindergarten
      default:
        return null; // Default for grade > 2
    }
  };

  // Helper: Get Math difficulty levels for current grade
  const getMathDifficultyLevels = (gradeLevel?: string) => {
    if (!gradeLevel) return [];
    
    const grade = gradeLevel.toLowerCase().replace(/[^0-9k]/g, '');
    
    for (const config of MATH_DIFFICULTY_LEVELS) {
      if (config.grades.includes(grade) || config.grades.includes(grade.toUpperCase())) {
        return config.levels;
      }
    }
    
    // Default to middle grades if not found
    return MATH_DIFFICULTY_LEVELS[1].levels;
  };

  // Helper: Get field labels based on subject
  const getFieldLabels = (subject: string) => {
    if (subject === 'Math') {
      return {
        difficulty: 'Problem Difficulty',
        contentType: 'Content Strand',
        contentLength: 'Number of Problems',
        contentSection: 'Instructional Content (I Do, We Do)',
        practiceSection: 'Practice Problems (You Do)'
      };
    }
    return {
      difficulty: 'Reading Level',
      contentType: 'Genre',
      contentLength: 'Paragraphs',
      contentSection: 'Introduction Passage',
      practiceSection: 'Lesson Introduction Activities'
    };
  };

  // Helper: Extract strand from selected standard
  const extractStrandFromStandard = (standard: Standard | null): string => {
    if (!standard || !standard.strand) return '';
    return standard.strand;
  };

  // Helper function to map teacher primarySubject to API subject parameter
  const mapSubjectToAPIParam = (primarySubject?: string): string | undefined => {
    if (!primarySubject) return undefined;
    
    const subjectMap: Record<string, string> = {
      'ELA': 'ELA',
      'Reading': 'ELA',
      'Math': 'Math',
      'Mathematics': 'Math',
      'Science': 'Science',
      'Social Studies': 'Social Studies'
    };
    
    return subjectMap[primarySubject] || primarySubject;
  };

  // Use custom hook to get teacher's standard name
  const { standardName: teachingStandardName, loading: standardLoading, error: standardError } = useTeacherStandard(teacher);
  
  // Filter standards by teacher's teaching standard
  const filteredStandards = useFilterStandardsByTeaching(standards, teachingStandardName, 'SmallGroupLessonPlan');

  // Helper function to capitalize the first letter of a string
  const capitalizeFirstLetter = (str: string | undefined | null): string => {
    if (!str || str.length === 0) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // Editing mode state
  const [isEditingMode, setIsEditingMode] = useState(false);
  const [originalLessonPlanId, setOriginalLessonPlanId] = useState<string | null>(null);

  // Load standards on component mount and when grade level, subject, or standards system changes
  useEffect(() => {
    const fetchStandards = async () => {
      try {
        setLoading(true);
        const params: any = {};
        if (teacher?.gradeLevel) params.gradeLevel = teacher.gradeLevel;
        if (selectedSubject) {
          // Map subject to API parameter format
          const subjectParam = mapSubjectToAPIParam(selectedSubject);
          if (subjectParam) {
            params.subject = subjectParam;
          }
        }
        
        const response = await apiAxiosInstance.get('/api/detailedstandards', { params });
        
        setStandards(response.data || []);
        // Clear selected standard when subject or grade changes
        setSelectedStandard(null);
      } catch (error) {
        console.error('Error fetching standards:', error);
        setError('Failed to load standards');
      } finally {
        setLoading(false);
      }
    };

    fetchStandards();
  }, [teacher?.gradeLevel, selectedSubject, teacher?.teachingStandards, teachingStandardName]);

  // Initialize lesson plan data when editing
  useEffect(() => {
    const state = location.state as { editingLessonPlan?: any; isEditing?: boolean } | null;
    
    if (state?.isEditing && state?.editingLessonPlan) {
      const existingPlan = state.editingLessonPlan;
      setIsEditingMode(true);
      setOriginalLessonPlanId(existingPlan._id);
      
      // Convert saved lesson plan format to component format
      const convertedPlan: LessonPlan = {
        id: existingPlan._id,
        form: {
          subject: existingPlan.form?.subject || 'ELA', // Default to ELA if missing
          standardId: existingPlan.standard?._id || '',
          // Legacy ELA fields
          lexileLevel: existingPlan.form?.lexileLevel || existingPlan.lexileLevel || '',
          genre: existingPlan.form?.genre || 'Fiction',
          paragraphCount: existingPlan.form?.paragraphCount || 3,
          // Generic fields (populate from legacy if available)
          difficulty: existingPlan.form?.difficulty || existingPlan.form?.lexileLevel || '',
          contentType: existingPlan.form?.contentType || existingPlan.form?.genre || 'Fiction',
          contentLength: existingPlan.form?.contentLength || existingPlan.form?.paragraphCount || 3,
          // Common fields
          topic: existingPlan.form?.topic || existingPlan.story?.title || '',
          independentPracticeTopic: existingPlan.form?.independentPracticeTopic || '',
          language: existingPlan.form?.language || 'english',
          revisingAndEditing: existingPlan.form?.revisingAndEditing || false,
        },
        sections: {
          introduction: {
            id: 'introduction',
            type: 'introduction' as const,
            story: existingPlan.sections?.introduction?.story || null,
            activities: existingPlan.sections?.introduction?.activities || '',
            // Load translated fields if present in saved plan
            storySpanish: existingPlan.sections?.introduction?.storySpanish || null,
            activitiesSpanish: existingPlan.sections?.introduction?.activitiesSpanish || undefined,
            isGenerated: existingPlan.sections?.introduction?.isGenerated || false,
            isStreaming: false,
          },
          independent: {
            id: 'independent',
            type: 'independent' as const,
            story: existingPlan.sections?.independent?.story || null,
            activities: existingPlan.sections?.independent?.activities || '',
            // Load translated fields if present in saved plan
            storySpanish: existingPlan.sections?.independent?.storySpanish || null,
            activitiesSpanish: existingPlan.sections?.independent?.activitiesSpanish || undefined,
            isGenerated: existingPlan.sections?.independent?.isGenerated || false,
            isStreaming: false,
          }
        },
        metadata: {
          createdAt: existingPlan.createdAt ? new Date(existingPlan.createdAt) : new Date(),
          updatedAt: existingPlan.updatedAt ? new Date(existingPlan.updatedAt) : new Date(),
          version: 1,
        }
      };

      // Handle legacy format (single story)
      if (existingPlan.story && !existingPlan.sections?.introduction && !existingPlan.sections?.independent) {
        convertedPlan.sections.introduction.story = existingPlan.story;
      }

      setLessonPlan(convertedPlan);
      
      // Set the subject based on form.subject first, then fall back to standard
      if (existingPlan.form?.subject) {
        setSelectedSubject(existingPlan.form.subject);
      } else if (existingPlan.standard?.subject) {
        // Convert from database format back to form format
        const dbSubject = existingPlan.standard.subject;
        if (dbSubject.includes('English') || dbSubject.includes('Reading')) {
          setSelectedSubject('ELA');
        } else if (dbSubject.includes('Math')) {
          setSelectedSubject('Math');
        }
      } else {
        // Default to ELA if no subject info available
        setSelectedSubject('ELA');
      }
      
      // Set the selected standard
      if (existingPlan.standard && standards.length > 0) {
        const matchingStandard = filteredStandards.find(s => 
          s._id === existingPlan.standard._id || 
          s.code === existingPlan.standard.code
        );
        if (matchingStandard) {
          setSelectedStandard(matchingStandard);
        }
      }

      // Collapse parameters section when editing
      setParametersExpanded(false);
      
      setSnackbar({
        open: true,
        message: `Loaded lesson plan for editing: ${existingPlan.form?.topic || existingPlan.story?.title || 'Untitled'}`,
        severity: 'info'
      });
    }
  }, [location.state, standards]);

  // Rotate placeholder examples for Content Modifier
  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex((prevIndex) => 
        (prevIndex + 1) % placeholderExamples.length
      );
    }, 3000); // Change every 3 seconds

    return () => clearInterval(interval);
  }, [placeholderExamples.length]);

  // Handle ResizeObserver errors
  useEffect(() => {
    const handleResizeObserverError = (e: ErrorEvent) => {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        e.stopImmediatePropagation();
        suppressResizeObserverError();
      }
    };

    window.addEventListener('error', handleResizeObserverError);
    
    return () => {
      window.removeEventListener('error', handleResizeObserverError);
    };
  }, []);

  // Auto-scroll chat to bottom when new messages are added
  useEffect(() => {
    if (chatHistoryRef.current) {
      const scrollElement = chatHistoryRef.current;
      setTimeout(() => {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }, 100);
    }
  }, [chatHistory]);

  // Initialize section-specific Lexile levels when form Lexile level changes
  useEffect(() => {
    if (lessonPlan.form.lexileLevel) {
      setSectionLexileLevels(prev => ({
        introduction: prev.introduction || lessonPlan.form.lexileLevel || '',
        independent: prev.independent || lessonPlan.form.lexileLevel || '',
        baseLexileLevel: prev.baseLexileLevel || lessonPlan.form.lexileLevel || ''
      }));
    }
  }, [lessonPlan.form.lexileLevel]);

  // Initialize section-specific paragraph counts when form paragraph count changes
  useEffect(() => {
    if (lessonPlan.form.paragraphCount) {
      setSectionParagraphCounts(prev => ({
        introduction: prev.introduction || lessonPlan.form.paragraphCount || 3,
        independent: prev.independent || lessonPlan.form.paragraphCount || 3,
        baseParagraphCount: prev.baseParagraphCount || lessonPlan.form.paragraphCount || 3
      }));
    }
  }, [lessonPlan.form.paragraphCount]);

  // Handle section updates from streaming generation
  const handleSectionUpdate = useCallback((
    sectionType: 'introduction' | 'independent',
    updates: Partial<LessonSection>
  ) => {
    setLessonPlan(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionType]: {
          ...prev.sections[sectionType],
          ...updates
        }
      },
      metadata: {
        ...prev.metadata,
        updatedAt: new Date(),
        version: (prev.metadata?.version || 1) + 1
      }
    }));
  }, []);

  // Handle form field updates
  const handleFormUpdate = (field: keyof LessonPlanForm, value: any) => {
    setLessonPlan(prev => ({
      ...prev,
      form: {
        ...prev.form,
        [field]: value
      }
    }));
  };

  /**
   * Converts word numbers to digit numbers in user requests
   * Helps backend better understand paragraph count modification requests
   * @param text - The user's request text
   * @returns Text with word numbers converted to digits
   */
  const convertWordNumbersToDigits = (text: string): string => {
    const wordToNumber: { [key: string]: string } = {
      'zero': '0', 'one': '1', 'two': '2', 'three': '3', 'four': '4', 'five': '5',
      'six': '6', 'seven': '7', 'eight': '8', 'nine': '9', 'ten': '10',
      'eleven': '11', 'twelve': '12', 'thirteen': '13', 'fourteen': '14', 'fifteen': '15',
      'sixteen': '16', 'seventeen': '17', 'eighteen': '18', 'nineteen': '19', 'twenty': '20'
    };

    let convertedText = text;
    
    // Convert word numbers to digits (case insensitive)
    Object.entries(wordToNumber).forEach(([word, digit]) => {
      const regex = new RegExp(`\\b${word}\\b`, 'gi');
      convertedText = convertedText.replace(regex, digit);
    });

    return convertedText;
  };

  /**
   * Detects if user request indicates increased rigor/difficulty
   * @param userRequest - The user's request text
   * @param currentSection - The section being modified
   * @returns Object indicating rigor increase and suggested Lexile level
   */
  const detectRigorIncrease = (userRequest: string, currentSection: 'introduction' | 'independent'): {
    hasRigorIncrease: boolean;
    rigorType: string;
    suggestedLexileIncrease: number;
    currentSectionLexileLevel: string;
    baseLexileLevel: string;
  } => {
    const request = userRequest.toLowerCase();
    
    // Keywords that indicate increased rigor/difficulty
    const rigorKeywords = [
      // Bloom's Taxonomy levels (higher order thinking)
      'analyze', 'analysis', 'analytical', 'evaluate', 'evaluation', 'create', 'synthesis',
      'compare', 'contrast', 'critique', 'justify', 'defend', 'argue', 'synthesize',
      'bloom', 'blooms', 'taxonomy', 'higher order', 'critical thinking',
      
      // Difficulty/Challenge keywords
      'challenging', 'challenge', 'difficult', 'harder', 'tougher', 'complex', 'complexity',
      'advanced', 'sophisticated', 'rigorous', 'rigor', 'demanding', 'intensive',
      'in-depth', 'detailed', 'thorough', 'comprehensive', 'elaborate',
      
      // Academic/Cognitive terms
      'inference', 'infer', 'deduce', 'conclude', 'interpret', 'interpretation',
      'abstract', 'conceptual', 'theoretical', 'philosophical', 'nuanced',
      'multilayered', 'multifaceted', 'intricate', 'sophisticated reasoning',
      
      // Higher level skills
      'problem solving', 'problem-solving', 'reasoning', 'logical', 'strategic',
      'metacognitive', 'metacognition', 'reflection', 'self-assessment',
      'transfer', 'application', 'synthesis', 'original', 'creative thinking',
      
      // Academic language
      'scholarly', 'academic', 'formal', 'technical', 'specialized', 'expert',
      'professional', 'research-based', 'evidence-based', 'data-driven'
    ];
    
    // Phrases that indicate increased difficulty
    const rigorPhrases = [
      'make it harder', 'make it more challenging', 'increase difficulty',
      'add complexity', 'more complex', 'more sophisticated', 'more advanced',
      'higher level', 'next level', 'step up', 'elevate', 'intensify',
      'more rigorous', 'more demanding', 'more thorough', 'deeper analysis',
      'critical analysis', 'higher order thinking', 'analytical thinking',
      'abstract reasoning', 'complex reasoning', 'sophisticated analysis'
    ];
    
    let hasRigorIncrease = false;
    let rigorType = '';
    let suggestedLexileIncrease = 0;
    
    // Check for rigor keywords
    const foundKeywords = rigorKeywords.filter(keyword => request.includes(keyword));
    const foundPhrases = rigorPhrases.filter(phrase => request.includes(phrase));
    
    if (foundKeywords.length > 0 || foundPhrases.length > 0) {
      hasRigorIncrease = true;
      
      // Determine rigor type and suggested Lexile increase
      if (foundKeywords.some(k => ['bloom', 'blooms', 'taxonomy', 'higher order', 'critical thinking'].includes(k))) {
        rigorType = 'Bloom\'s Taxonomy (Higher Order Thinking)';
        suggestedLexileIncrease = 2; // Increase by 2 levels
      } else if (foundKeywords.some(k => ['analyze', 'analysis', 'evaluate', 'synthesis', 'critical'].includes(k))) {
        rigorType = 'Critical Analysis';
        suggestedLexileIncrease = 2; // Increase by 2 levels
      } else if (foundKeywords.some(k => ['challenging', 'complex', 'advanced', 'sophisticated', 'rigorous'].includes(k))) {
        rigorType = 'Increased Complexity';
        suggestedLexileIncrease = 1; // Increase by 1 level
      } else if (foundPhrases.length > 0) {
        rigorType = 'General Difficulty Increase';
        suggestedLexileIncrease = 1; // Increase by 1 level
      } else {
        rigorType = 'Academic Enhancement';
        suggestedLexileIncrease = 1; // Increase by 1 level
      }
      
      // Cap the increase to prevent going beyond maximum level
      // Use base level as the starting point for calculations
      const baseLexileIndex = lexileLevels.findIndex(level => level.level === sectionLexileLevels.baseLexileLevel);
      const maxIncrease = lexileLevels.length - 1 - baseLexileIndex;
      suggestedLexileIncrease = Math.min(suggestedLexileIncrease, maxIncrease);
    }
    
    return {
      hasRigorIncrease,
      rigorType,
      suggestedLexileIncrease,
      currentSectionLexileLevel: sectionLexileLevels[currentSection] || sectionLexileLevels.baseLexileLevel,
      baseLexileLevel: sectionLexileLevels.baseLexileLevel
    };
  };

  /**
   * Automatically increases Lexile level based on rigor detection for a specific section
   * @param increaseAmount - Number of levels to increase
   * @param section - The section being modified
   * @returns The new Lexile level for the section
   */
  const increaseSectionLexileLevel = (increaseAmount: number, section: 'introduction' | 'independent'): string => {
    // Always calculate from the base level, not the current section level
    const baseLexileIndex = lexileLevels.findIndex(level => level.level === sectionLexileLevels.baseLexileLevel);
    const newLexileIndex = Math.min(baseLexileIndex + increaseAmount, lexileLevels.length - 1);
    return lexileLevels[newLexileIndex].level;
  };

  /**
   * Analyzes user requests to determine which content sections should be modified
   * This prevents questions from being incorrectly added to story content
   * @param userRequest - The user's request text
   * @returns Object indicating which sections should be modified
   */
  const analyzeUserRequest = (userRequest: string): { 
    shouldModifyStory: boolean; 
    shouldModifyActivities: boolean; 
    requestType: 'story-only' | 'activities-only' | 'both';
    reasoning: string;
    rigorAnalysis: {
      hasRigorIncrease: boolean;
      rigorType: string;
      suggestedLexileIncrease: number;
    };
    poetryAnalysis?: {
      isPoetryRequest: boolean;
      stanzaModification: boolean;
      stanzaCount?: number;
      poetryInstructions?: string;
    };
  } => {
    const request = userRequest.toLowerCase();
    
    // Get rigor analysis - Note: This is called during routing analysis, we'll get the section later
    const rigorAnalysis = detectRigorIncrease(userRequest, 'introduction'); // Default to introduction for initial analysis
    
    // POETRY-SPECIFIC ANALYSIS
    const poetryAnalysis = (() => {
      const isPoetryRequest = request.includes('stanza') || request.includes('stanzas') || 
                             request.includes('poem') || request.includes('poetry') ||
                             request.includes('verse') || request.includes('verses');
      
      if (!isPoetryRequest) {
        return {
          isPoetryRequest: false,
          stanzaModification: false
        };
      }

      // Detect stanza modification requests
      const stanzaModification = request.includes('stanza') || request.includes('stanzas');
      
      // Extract stanza count if specified
      let stanzaCount: number | undefined;
      const stanzaMatch = request.match(/(?:add|remove|more|less)\s+(\d+)\s+(?:stanza|stanzas)/);
      if (stanzaMatch) {
        stanzaCount = parseInt(stanzaMatch[1]);
      }

      // Generate poetry-specific instructions
      const poetryInstructions = stanzaModification ? `
CRITICAL POETRY MODIFICATION RULES:
- Current genre is Poetry - this is a POEM, not a story
- STANZA = PARAGRAPH for poetry: "add a stanza" means "add a paragraph of poetry"
- "Add another stanza" = Add one more verse/paragraph to the poem
- "Add 2 stanzas" = Add 2 more verses/paragraphs to the poem
- Each stanza must be separated by double line breaks (\\n\\n)
- Maintain the poem's rhythm, rhyme scheme, and thematic flow
- Count stanzas the same way you count paragraphs in regular text
- When adding stanzas, continue the poem's narrative or thematic development

CRITICAL FORMAT PRESERVATION:
- DO NOT convert poetry to prose/paragraph format
- PRESERVE the line-by-line structure within each stanza
- MAINTAIN the exact poetic formatting of the existing content
- NEW stanzas should match the style and structure of existing stanzas
- Keep all original line breaks and poetic formatting intact

EXAMPLE STANZA ADDITION:
If the current poem has stanzas like:
"Friendship is a garden bright,
Where kindness blooms in morning light.

Like puzzle pieces, we connect,
Each one with a special effect."

Then adding "another stanza" should result in:
"Friendship is a garden bright,
Where kindness blooms in morning light.

Like puzzle pieces, we connect,
Each one with a special effect.

When troubles come, we share the load,
Together walking a strong road."

MAINTAIN EXACT POETRY FORMAT - DO NOT CHANGE TO PROSE!
` : '';

      return {
        isPoetryRequest: true,
        stanzaModification,
        stanzaCount,
        poetryInstructions
      };
    })();
    
    // Keywords that indicate activities/questions/assessments (should NOT go to story)
    const activitiesKeywords = [
      // English keywords
      'question', 'questions', 'quiz', 'assessment', 'test', 'evaluate', 'comprehension',
      'multiple choice', 'open ended', 'true/false', 'fill in the blank', 'matching',
      'vocabulary practice', 'word bank', 'exercise', 'activity', 'activities',
      'discussion prompt', 'writing prompt', 'reflection', 'analysis',
      'critical thinking', 'problem solving', 'application', 'synthesis',
      'graphic organizer', 'worksheet', 'homework', 'assignment',
      'pre-reading', 'post-reading', 'during reading', 'instruction', 'instructions',
      'teaching', 'lesson', 'learning objective', 'standard', 'rubric',
      'answer key', 'solution', 'example', 'practice', 'drill',
      // Spanish keywords
      'pregunta', 'preguntas', 'cuestionario', 'evaluación', 'evaluacion', 'examen', 'evaluar', 'comprensión', 'comprension',
      'opción múltiple', 'opcion multiple', 'multiple', 'múltiple', 'respuesta abierta', 'verdadero/falso', 'verdadero falso',
      'llenar el espacio', 'completar', 'emparejar', 'relacionar', 'vocabulario', 'práctica', 'practica',
      'banco de palabras', 'ejercicio', 'actividad', 'actividades',
      'pregunta de discusión', 'pregunta de escritura', 'reflexión', 'reflexion', 'análisis', 'analisis',
      'pensamiento crítico', 'pensamiento critico', 'resolución de problemas', 'resolucion de problemas',
      'aplicación', 'aplicacion', 'síntesis', 'sintesis',
      'organizador gráfico', 'organizador grafico', 'hoja de trabajo', 'tarea', 'asignación', 'asignacion',
      'pre-lectura', 'post-lectura', 'durante la lectura', 'instrucción', 'instruccion', 'instrucciones',
      'enseñanza', 'ensenanza', 'lección', 'leccion', 'objetivo de aprendizaje', 'estándar', 'estandar', 'rúbrica', 'rubrica',
      'clave de respuestas', 'solución', 'solucion', 'ejemplo', 'práctica', 'practica', 'ejercitación', 'ejercitacion'
    ];
    
    // Keywords that indicate story modifications (should go to story)
    const storyKeywords = [
      // English keywords
      'story', 'narrative', 'plot', 'character', 'characters', 'setting', 'dialogue',
      'description', 'descriptive', 'vivid', 'engaging', 'interesting', 'exciting',
      'suspenseful', 'dramatic', 'emotional', 'detail', 'details', 'imagery',
      'scene', 'chapter', 'paragraph', 'paragraphs', 'sentence', 'sentences',
      'beginning', 'middle', 'end', 'conclusion', 'introduction', 'transition',
      'flow', 'pacing', 'tone', 'mood', 'voice', 'perspective', 'point of view',
      'conflict', 'resolution', 'climax', 'theme', 'message', 'moral',
      'longer', 'shorter', 'expand', 'elaborate', 'summarize', 'condense',
      'rewrite', 'revise', 'edit', 'improve', 'enhance', 'polish',
      // Add poetry-specific keywords
      'stanza', 'stanzas', 'poem', 'poetry', 'verse', 'verses', 'rhyme', 'rhythm',
      'meter', 'poetic', 'poet', 'poetic device', 'figurative language',
      // Spanish keywords
      'historia', 'cuento', 'narrativa', 'trama', 'personaje', 'personajes', 'ambiente', 'escenario', 'diálogo', 'dialogo',
      'descripción', 'descripcion', 'descriptivo', 'vívido', 'vivido', 'atractivo', 'interesante', 'emocionante',
      'suspenso', 'dramático', 'dramatico', 'emocional', 'detalle', 'detalles', 'imágenes', 'imagenes',
      'escena', 'capítulo', 'capitulo', 'párrafo', 'parrafo', 'párrafos', 'parrafos', 'oración', 'oracion', 'oraciones',
      'comienzo', 'inicio', 'medio', 'final', 'conclusión', 'conclusion', 'introducción', 'introduccion', 'transición', 'transicion',
      'flujo', 'ritmo', 'tono', 'estado de ánimo', 'estado de animo', 'voz', 'perspectiva', 'punto de vista',
      'conflicto', 'resolución', 'resolucion', 'clímax', 'climax', 'tema', 'mensaje', 'moral',
      'más largo', 'mas largo', 'más corto', 'mas corto', 'expandir', 'elaborar', 'resumir', 'condensar',
      'reescribir', 'revisar', 'editar', 'mejorar', 'realzar', 'pulir',
      // Spanish poetry-specific keywords
      'estrofa', 'estrofas', 'poema', 'poesía', 'poesia', 'verso', 'versos', 'rima', 'ritmo',
      'métrica', 'metrica', 'poético', 'poetico', 'poeta', 'recurso poético', 'recurso poetico', 'lenguaje figurado'
    ];
    
    // Check for activities-only requests
    const hasActivitiesKeywords = activitiesKeywords.some(keyword => request.includes(keyword));
    const hasStoryKeywords = storyKeywords.some(keyword => request.includes(keyword));
    
    // Special patterns that are clearly activities-only
    const isActivitiesOnly = 
      // English patterns
      request.includes('add') && (request.includes('question') || request.includes('quiz') || request.includes('assessment')) ||
      request.includes('create') && (request.includes('question') || request.includes('activity') || request.includes('exercise')) ||
      request.includes('multiple choice') || request.includes('open ended') || request.includes('true/false') ||
      request.includes('comprehension') && (request.includes('question') || request.includes('check')) ||
      request.includes('vocabulary') && (request.includes('practice') || request.includes('exercise') || request.includes('activity')) ||
      request.includes('discussion prompt') || request.includes('writing prompt') ||
      request.includes('graphic organizer') || request.includes('worksheet') ||
      request.includes('teaching') || request.includes('instruction') || request.includes('lesson plan') ||
      // Spanish patterns
      (request.includes('agrega') || request.includes('añade') || request.includes('anade')) && (request.includes('pregunta') || request.includes('preguntas') || request.includes('cuestionario') || request.includes('evaluación') || request.includes('evaluacion')) ||
      (request.includes('crea') || request.includes('crear')) && (request.includes('pregunta') || request.includes('preguntas') || request.includes('actividad') || request.includes('actividades') || request.includes('ejercicio')) ||
      request.includes('opción múltiple') || request.includes('opcion multiple') || request.includes('múltiple') || request.includes('multiple') ||
      request.includes('respuesta abierta') || request.includes('verdadero/falso') || request.includes('verdadero falso') ||
      request.includes('comprensión') && (request.includes('pregunta') || request.includes('preguntas') || request.includes('verificar')) ||
      request.includes('comprension') && (request.includes('pregunta') || request.includes('preguntas') || request.includes('verificar')) ||
      request.includes('vocabulario') && (request.includes('práctica') || request.includes('practica') || request.includes('ejercicio') || request.includes('actividad')) ||
      request.includes('pregunta de discusión') || request.includes('pregunta de escritura') ||
      request.includes('organizador gráfico') || request.includes('organizador grafico') || request.includes('hoja de trabajo') ||
      request.includes('enseñanza') || request.includes('ensenanza') || request.includes('instrucción') || request.includes('instruccion') || request.includes('plan de lección') || request.includes('plan de leccion');
    
    // Special patterns that are clearly story-only  
    const isStoryOnly =
      // English patterns
      request.includes('story') && (request.includes('longer') || request.includes('shorter') || request.includes('more') || request.includes('less')) ||
      request.includes('paragraph') && (request.includes('add') || request.includes('remove') || request.includes('more') || request.includes('fewer')) ||
      request.includes('character') && (request.includes('develop') || request.includes('describe') || request.includes('add')) ||
      request.includes('dialogue') || request.includes('description') || request.includes('detail') ||
      request.includes('setting') && (request.includes('describe') || request.includes('expand') || request.includes('detail')) ||
      request.includes('rewrite') || request.includes('revise') || request.includes('edit') && !request.includes('question') ||
      request.includes('engaging') || request.includes('interesting') || request.includes('exciting') ||
      request.includes('improve') && request.includes('story') && !request.includes('question') ||
      // Add poetry-specific patterns
      request.includes('stanza') && (request.includes('add') || request.includes('remove') || request.includes('more') || request.includes('fewer')) ||
      request.includes('poem') && (request.includes('longer') || request.includes('shorter') || request.includes('expand') || request.includes('condense')) ||
      request.includes('poetry') && (request.includes('modify') || request.includes('change') || request.includes('improve')) ||
      request.includes('verse') && (request.includes('add') || request.includes('remove') || request.includes('more') || request.includes('fewer')) ||
      // Spanish patterns
      (request.includes('historia') || request.includes('cuento')) && (request.includes('más largo') || request.includes('mas largo') || request.includes('más corto') || request.includes('mas corto') || request.includes('más') || request.includes('mas') || request.includes('menos')) ||
      (request.includes('párrafo') || request.includes('parrafo') || request.includes('párrafos') || request.includes('parrafos')) && (request.includes('agrega') || request.includes('añade') || request.includes('anade') || request.includes('elimina') || request.includes('quita') || request.includes('más') || request.includes('mas') || request.includes('menos')) ||
      (request.includes('personaje') || request.includes('personajes')) && (request.includes('desarrolla') || request.includes('describe') || request.includes('agrega') || request.includes('añade') || request.includes('anade')) ||
      request.includes('diálogo') || request.includes('dialogo') || request.includes('descripción') || request.includes('descripcion') || request.includes('detalle') || request.includes('detalles') ||
      (request.includes('ambiente') || request.includes('escenario')) && (request.includes('describe') || request.includes('expandir') || request.includes('detalle')) ||
      request.includes('reescribir') || request.includes('revisar') || request.includes('editar') && !request.includes('pregunta') && !request.includes('preguntas') ||
      request.includes('atractivo') || request.includes('interesante') || request.includes('emocionante') ||
      request.includes('mejorar') && (request.includes('historia') || request.includes('cuento')) && !request.includes('pregunta') && !request.includes('preguntas') ||
      // Spanish poetry-specific patterns
      request.includes('estrofa') && (request.includes('agrega') || request.includes('añade') || request.includes('anade') || request.includes('elimina') || request.includes('quita') || request.includes('más') || request.includes('mas') || request.includes('menos')) ||
      request.includes('poema') && (request.includes('más largo') || request.includes('mas largo') || request.includes('más corto') || request.includes('mas corto') || request.includes('expandir') || request.includes('condensar')) ||
      (request.includes('poesía') || request.includes('poesia')) && (request.includes('modificar') || request.includes('cambiar') || request.includes('mejorar')) ||
      request.includes('verso') && (request.includes('agrega') || request.includes('añade') || request.includes('anade') || request.includes('elimina') || request.includes('quita') || request.includes('más') || request.includes('mas') || request.includes('menos'));
    
    // Determine routing
    if (isActivitiesOnly) {
      // Check if request is in Spanish for appropriate reasoning message
      const isSpanishRequest = request.includes('pregunta') || request.includes('preguntas') || request.includes('agrega') || 
                              request.includes('crea') || request.includes('actividad') || request.includes('actividades') ||
                              request.includes('ejercicio') || request.includes('múltiple') || request.includes('multiple') ||
                              request.includes('evaluación') || request.includes('evaluacion');
      
      return {
        shouldModifyStory: false,
        shouldModifyActivities: true,
        requestType: 'activities-only',
        reasoning: isSpanishRequest ? 
          'Request specifically asks for questions, assessments, or educational activities (Spanish)' :
          'Request specifically asks for questions, assessments, or educational activities',
        rigorAnalysis,
        poetryAnalysis
      };
    } else if (isStoryOnly) {
      // Check if request is in Spanish for appropriate reasoning message
      const isSpanishRequest = request.includes('historia') || request.includes('cuento') || request.includes('párrafo') || 
                              request.includes('parrafo') || request.includes('personaje') || request.includes('personajes') ||
                              request.includes('estrofa') || request.includes('poema') || request.includes('poesía') || request.includes('poesia');
      
      return {
        shouldModifyStory: true,
        shouldModifyActivities: false,
        requestType: 'story-only',
        reasoning: poetryAnalysis.isPoetryRequest ? 
          (isSpanishRequest ? 'Request specifically asks for poetry/stanza content modifications (Spanish)' : 'Request specifically asks for poetry/stanza content modifications') : 
          (isSpanishRequest ? 'Request specifically asks for story content modifications (Spanish)' : 'Request specifically asks for story content modifications'),
        rigorAnalysis,
        poetryAnalysis
      };
    } else if (hasActivitiesKeywords && !hasStoryKeywords) {
      return {
        shouldModifyStory: false,
        shouldModifyActivities: true,
        requestType: 'activities-only',
        reasoning: 'Request contains activities keywords but no story keywords',
        rigorAnalysis,
        poetryAnalysis
      };
    } else if (hasStoryKeywords && !hasActivitiesKeywords) {
      return {
        shouldModifyStory: true,
        shouldModifyActivities: false,
        requestType: 'story-only',
        reasoning: poetryAnalysis.isPoetryRequest ? 
          'Request contains poetry keywords but no activities keywords' : 
          'Request contains story keywords but no activities keywords',
        rigorAnalysis,
        poetryAnalysis
      };
    } else {
      // Default: modify both (for ambiguous requests)
      return {
        shouldModifyStory: true,
        shouldModifyActivities: true,
        requestType: 'both',
        reasoning: 'Request is ambiguous or could apply to both story and activities',
        rigorAnalysis,
        poetryAnalysis
      };
    }
  };

  // Initialize streaming generation hook
  const {
    generateIntroductionSection,
    generateIndependentSection,
    generateBothSections,
    regenerateContent,
    cancelGeneration: cancelGenerationHook,
    streamingStates,
    streamingContent,
    isGenerating
  } = useStreamingGeneration(
    handleSectionUpdate,
    useMemo(() => {
      if (selectedStandard) {
        return {
          code: selectedStandard.standard || selectedStandard.code,
          description: selectedStandard.description || selectedStandard.short_description || '',
          _id: selectedStandard._id
        };
      }
      return null;
    }, [selectedStandard])
  );

  // ENHANCED story content updates with comprehensive state management
  const handleStoryUpdate = useCallback((
    sectionType: 'introduction' | 'independent',
    content: string
  ) => {
    console.log('🚀 ENHANCED handleStoryUpdate called:', { 
      sectionType, 
      contentLength: content.length,
      preview: content.substring(0, 100) + '...',
      timestamp: new Date().toISOString()
    });
    
    // Calculate paragraph/stanza count for this specific section
    const paragraphCount = content.trim() ? content.trim().split('\n\n').filter(p => p.trim()).length : 0;
    
    // Update section-specific paragraph count
    setSectionParagraphCounts(prev => ({
      ...prev,
      [sectionType]: paragraphCount
    }));
    
    setLessonPlan(prev => {
      const existingStory = prev.sections[sectionType].story;
      
      // Create new story object with enhanced metadata
      const wordCount = content.trim().split(/\s+/).length;
      const newStory = existingStory ? {
        ...existingStory,
        content: content.trim(),
        metadata: {
          ...existingStory.metadata,
          wordCount,
          readingTimeMinutes: Math.ceil(wordCount / 150), // Approximate reading time
        }
      } : {
        id: `${sectionType}-story-${Date.now()}`,
        title: `${sectionType === 'introduction' ? 'Introduction' : 'Independent Practice'} Story`,
        content: content.trim(),
        metadata: {
          id: `${sectionType}-story-metadata-${Date.now()}`,
          title: `${sectionType === 'introduction' ? 'Introduction' : 'Independent Practice'} Story`,
          wordCount,
          readingTimeMinutes: Math.ceil(wordCount / 150),
          createdAt: new Date()
        }
      };

      // Create new state object to force re-render
      const updated = {
        ...prev,
        sections: {
          ...prev.sections,
          [sectionType]: {
            ...prev.sections[sectionType],
            story: newStory,
            isGenerated: true,
            isStreaming: false, // Ensure streaming is cleared
            lastModified: new Date().toISOString()
          }
        },
        metadata: {
          ...prev.metadata,
          updatedAt: new Date(),
          version: (prev.metadata.version || 1) + 1
        }
      };

      console.log('✅ ENHANCED story update complete:', { 
        sectionType, 
        hasStory: !!updated.sections[sectionType].story,
        storyLength: updated.sections[sectionType].story?.content?.length || 0,
        wordCount: updated.sections[sectionType].story?.metadata?.wordCount || 0,
        paragraphCount: paragraphCount,
        stateVersion: updated.metadata.version
      });

      return updated;
    });
  }, []);

  // ENHANCED activities content updates with comprehensive state management
  const handleActivitiesUpdate = useCallback((
    sectionType: 'introduction' | 'independent',
    content: string
  ) => {
    console.log('🚀 ENHANCED handleActivitiesUpdate called:', { 
      sectionType, 
      contentLength: content.length,
      preview: content.substring(0, 100) + '...',
      timestamp: new Date().toISOString()
    });
    
    setLessonPlan(prev => {
      // Create new state object to force re-render
      const updated = {
        ...prev,
        sections: {
          ...prev.sections,
          [sectionType]: {
            ...prev.sections[sectionType],
            activities: content.trim(),
            isGenerated: true,
            isStreaming: false, // Ensure streaming is cleared
            lastModified: new Date().toISOString()
          }
        },
        metadata: {
          ...prev.metadata,
          updatedAt: new Date(),
          version: (prev.metadata.version || 1) + 1
        }
      };

      console.log('✅ ENHANCED activities update complete:', { 
        sectionType, 
        activitiesLength: updated.sections[sectionType].activities?.length || 0,
        stateVersion: updated.metadata.version
      });

      return updated;
    });
  }, []);

  // Validate form before generation
  const validateForm = (): boolean => {
    const { standardId, difficulty, contentType, contentLength } = lessonPlan.form;

    // Check standard is selected
    if (!standardId) {
      setSnackbar({
        open: true,
        message: 'Please select a learning standard before generating.',
        severity: 'warning'
      });
      return false;
    }

    // Check subject-specific required fields
    if (selectedSubject === 'Math') {
      // Math requires difficulty, contentType (strand), and contentLength (problems)
      if (!difficulty || !contentType || !contentLength) {
        setSnackbar({
          open: true,
          message: 'Please fill in all required fields (Difficulty, Content Strand, Number of Problems) before generating.',
          severity: 'warning'
        });
        return false;
      }
    } else {
      // ELA requires lexileLevel, genre, and paragraphCount
      const { lexileLevel, genre, paragraphCount } = lessonPlan.form;
      if (!lexileLevel || !genre || !paragraphCount) {
        setSnackbar({
          open: true,
          message: 'Please fill in all required fields (Reading Level, Genre, Text Length) before generating.',
          severity: 'warning'
        });
        return false;
      }
    }

    return true;
  };

  // Wrap cancelGeneration to reload page for clean state
  const cancelGeneration = useCallback(() => {
    console.log('🛑 Cancel Generation clicked');
    
    // Call the hook's cancel function to abort requests
    cancelGenerationHook();
    
    // Show cancellation message
      setSnackbar({
        open: true,
      message: 'Generation cancelled - reloading page...',
        severity: 'info'
      });

    // Reload the page to ensure clean state
    setTimeout(() => {
      window.location.reload();
    }, 1000); // Give time for the message to show
  }, [cancelGenerationHook]);

  // Check if sections have content
  const hasIntroductionContent = !!(lessonPlan.sections.introduction.story?.content || 
                                   lessonPlan.sections.introduction.activities);
  const hasIndependentContent = !!(lessonPlan.sections.independent.story?.content || 
                                   lessonPlan.sections.independent.activities);
  
  // Check if all content has been generated (both sections have content)
  const hasAllContentGenerated = hasIntroductionContent && hasIndependentContent;

  // Handle generation button click (for testing, log to console)
  const handleGenerate = useCallback(async () => {
    if (!validateForm()) return;

    // Check if there's existing content that would be replaced
    const hasExistingContent = hasIntroductionContent || hasIndependentContent;
    
    if (hasExistingContent) {
      // Show confirmation dialog
      setConfirmGenerateOpen(true);
      return;
    }

    // If no existing content, proceed with generation
    await proceedWithGeneration();
  }, [lessonPlan, generateBothSections, streamingStates, streamingContent, hasIntroductionContent, hasIndependentContent]);

  // Proceed with generation after confirmation
  const proceedWithGeneration = useCallback(async () => {
    // Collapse parameters section when generation starts
    setParametersExpanded(false);

    // Backup current content before generation starts
    setGenerationBackup({
      introduction: {
        story: lessonPlan.sections.introduction.story?.content || null,
        activities: lessonPlan.sections.introduction.activities || null
      },
      independent: {
        story: lessonPlan.sections.independent.story?.content || null,
        activities: lessonPlan.sections.independent.activities || null
      }
    });
    console.log('💾 Backed up current content before generation');

    try {
      setSnackbar({
        open: true,
        message: 'Starting lesson plan generation...',
        severity: 'info'
      });

      console.log('🚀 Generating lesson plan with streaming:', lessonPlan.form);
      console.log('📊 Streaming states available:', streamingStates);
      console.log('📝 Streaming content available:', streamingContent);
      
      // Test the streaming generation 
      await generateBothSections(lessonPlan);
      
      // Clear backup after successful generation
      setGenerationBackup(null);
      console.log('🗑️ Cleared generation backup after success');
      
      setSnackbar({
        open: true,
        message: 'Lesson plan generation completed (check console for streaming logs)!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Generation error:', error);
      
      // Don't show error for user cancellation (backup will be restored by cancelGeneration)
      if (error instanceof Error && (error.name === 'AbortError' || error.message.includes('cancel'))) {
        console.log('Generation was cancelled by user');
        return;
      }
      
      // For non-cancellation errors, restore backup if available
      if (generationBackup) {
        console.log('🔄 Restoring content from backup after error');
        
        if (generationBackup.introduction.story !== null) {
          handleStoryUpdate('introduction', generationBackup.introduction.story);
        } else {
          handleSectionUpdate('introduction', { story: null });
        }
        
        if (generationBackup.introduction.activities !== null) {
          handleActivitiesUpdate('introduction', generationBackup.introduction.activities);
        } else {
          handleSectionUpdate('introduction', { activities: '' });
        }
        
        if (generationBackup.independent.story !== null) {
          handleStoryUpdate('independent', generationBackup.independent.story);
        } else {
          handleSectionUpdate('independent', { story: null });
        }
        
        if (generationBackup.independent.activities !== null) {
          handleActivitiesUpdate('independent', generationBackup.independent.activities);
        } else {
          handleSectionUpdate('independent', { activities: '' });
        }
        
        setGenerationBackup(null);
      }
      
      setSnackbar({
        open: true,
        message: 'Generation ready - backend API needs to be implemented. Check console for details.',
        severity: 'warning'
      });
    }
  }, [lessonPlan, generateBothSections, streamingStates, streamingContent, generationBackup, handleStoryUpdate, handleActivitiesUpdate, handleSectionUpdate]);

  // Check if any streaming is active for individual sections
  const hasAnyStreamingActive = Object.values(streamingStates).some(state => state.isActive);
  
  // Handle cancelling chat processing
  const handleCancelChat = useCallback(() => {
    if (chatAbortController) {
      chatAbortController.abort();
      setChatAbortController(null);
    }
    setIsChatProcessing(false);
    
    // Clear translation regenerating flag
    if (contentBackup) {
      setTranslationsRegenerating(prev => ({ ...prev, [contentBackup.section]: false }));
    }
    
    // Restore content from backup if available
    if (contentBackup) {
      console.log('🔄 Restoring content from backup after cancellation');
      
      const currentSection = lessonPlan.sections[contentBackup.section];
      
      // Restore story if it was being modified
      if (contentBackup.story !== null) {
        handleStoryUpdate(contentBackup.section, contentBackup.story);
      }
      
      // Restore activities if they were being modified
      if (contentBackup.activities !== null) {
        handleActivitiesUpdate(contentBackup.section, contentBackup.activities);
      }
      
      // Clear streaming state
      handleSectionUpdate(contentBackup.section, {
        isStreaming: false
      });
      
      setContentBackup(null);
    }
    
    // Add cancelled message to chat
    const cancelledMessage = {
      id: Date.now().toString(),
      message: 'Modification cancelled.',
      timestamp: new Date(),
      type: 'system' as const
    };
    setChatHistory(prev => [...prev, cancelledMessage]);
  }, [chatAbortController, contentBackup, lessonPlan.sections, handleStoryUpdate, handleActivitiesUpdate, handleSectionUpdate]);

  // Comprehensive disabled state for action buttons (save, download, print)
  const areActionButtonsDisabled = loading || isGenerating || isChatProcessing || hasAnyStreamingActive;

  // Handle chat message submission
  const handleChatSubmit = useCallback(async () => {
    if (!chatMessage.trim()) return;

    const userMessage = {
      id: Date.now().toString(),
      message: chatMessage.trim(),
      timestamp: new Date(),
      type: 'user' as const
    };

    setChatHistory(prev => [...prev, userMessage]);
    setChatMessage('');
    setIsChatProcessing(true);
    
    // Create abort controller for this chat session
    const abortController = new AbortController();
    setChatAbortController(abortController);
    
    // Hide translation immediately when modification starts (if translations exist)
    const hasTranslations = activeTab === 'introduction' 
      ? (lessonPlan.sections.introduction.storySpanish || lessonPlan.sections.introduction.activitiesSpanish)
      : (lessonPlan.sections.independent.storySpanish || lessonPlan.sections.independent.activitiesSpanish);
    
    if (hasTranslations) {
      setTranslationsRegenerating(prev => ({ ...prev, [activeTab]: true }));
      console.log(`🌐 Hiding ${activeTab} translations during modification`);
    }

    try {
      console.log('🎯 Starting sequential modification with new endpoints for:', activeTab);
      
      // Determine which content is available for modification
      const currentSection = activeTab === 'introduction' ? 'Introduction' : 'Independent Practice';
      const currentStory = activeTab === 'introduction' 
        ? lessonPlan.sections.introduction.story?.content 
        : lessonPlan.sections.independent.story?.content;
      const currentActivities = activeTab === 'introduction'
        ? lessonPlan.sections.introduction.activities
        : lessonPlan.sections.independent.activities;

      // Check if there's any content to modify (need at least story OR activities)
      if (!currentStory && !currentActivities) {
        const noContentMessage = {
          id: (Date.now() + 1).toString(),
          message: `No content in ${currentSection} section. Generate content first.`,
          timestamp: new Date(),
          type: 'system' as const
        };
        
        setTimeout(() => {
          setChatHistory(prev => [...prev, noContentMessage]);
          setIsChatProcessing(false);
        }, 1000);
        return;
      }

      // Backup current content before modifications so we can restore if cancelled
      setContentBackup({
        section: activeTab,
        story: currentStory || null,
        activities: currentActivities || null
      });
      console.log('💾 Backed up current content before modification');

      let modifiedStoryContent = currentStory;
      let modifiedActivitiesContent = currentActivities;
      let appliedModifications: string[] = [];
      let hasStoryChanges = false;
      let hasActivitiesChanges = false;

      // SMART ROUTING: Analyze user request to determine which sections to modify
      const routingAnalysis = analyzeUserRequest(userMessage.message);
      console.log('🧠 Smart routing analysis:', {
        userRequest: userMessage.message.substring(0, 100) + '...',
        shouldModifyStory: routingAnalysis.shouldModifyStory,
        shouldModifyActivities: routingAnalysis.shouldModifyActivities,
        requestType: routingAnalysis.requestType,
        reasoning: routingAnalysis.reasoning,
        poetryAnalysis: routingAnalysis.poetryAnalysis
      });

      // Handle automatic Lexile level increase for rigor increases (section-specific)
      let lexileLevelAdjusted = false;
      let originalSectionLexileLevel = sectionLexileLevels[activeTab] || sectionLexileLevels.baseLexileLevel;
      let newSectionLexileLevel = originalSectionLexileLevel;
      
      // Re-analyze rigor for the specific current section
      const sectionSpecificRigorAnalysis = detectRigorIncrease(userMessage.message, activeTab);
      
      if (sectionSpecificRigorAnalysis.hasRigorIncrease && sectionSpecificRigorAnalysis.suggestedLexileIncrease > 0) {
        newSectionLexileLevel = increaseSectionLexileLevel(sectionSpecificRigorAnalysis.suggestedLexileIncrease, activeTab);
        
        if (newSectionLexileLevel !== originalSectionLexileLevel) {
          lexileLevelAdjusted = true;
          
          // Update the section-specific Lexile level
          setSectionLexileLevels(prev => ({
            ...prev,
            [activeTab]: newSectionLexileLevel
          }));
          
          // Notify user about the automatic Lexile level increase
          setChatHistory(prev => [...prev, {
            id: Date.now().toString(),
            message: `Lexile level adjusted: ${originalSectionLexileLevel} → ${newSectionLexileLevel}`,
            timestamp: new Date(),
            type: 'system' as const
          }]);
        }
      }

      // Show user which sections will be modified
      const poetryInfo = routingAnalysis.poetryAnalysis?.isPoetryRequest ? 
        `\n📝 Poetry detected: ${routingAnalysis.poetryAnalysis.stanzaModification ? 'Stanza modification' : 'Poetry content'}${routingAnalysis.poetryAnalysis.stanzaCount ? ` (${routingAnalysis.poetryAnalysis.stanzaCount} stanzas)` : ''}` : '';
      
      setChatHistory(prev => [...prev, {
        id: Date.now().toString(),
        message: `Modifying: ${routingAnalysis.requestType === 'story-only' ? 'Story' : routingAnalysis.requestType === 'activities-only' ? 'Activities' : 'Story and Activities'}`,
        timestamp: new Date(),
        type: 'system' as const
      }]);

      // PHASE 1: Story Modification (if needed based on smart routing)
      if (routingAnalysis.shouldModifyStory) {
        console.log('🚀 Phase 1: Attempting story modification (smart routing approved)');
        console.log('DEBUG: Current story content:', currentStory?.substring(0, 100) + '...');
        console.log('DEBUG: Modified story content:', modifiedStoryContent?.substring(0, 100) + '...');
        console.log('DEBUG: Current paragraph count:', currentStory ? currentStory.split('\n\n').filter(p => p.trim()).length : 0);
        
        // Mark as streaming for visual feedback - set both story and activities as streaming
        handleSectionUpdate(activeTab, {
          story: currentStory && lessonPlan.sections[activeTab].story ? { 
            ...lessonPlan.sections[activeTab].story!, 
            content: '' 
          } : null,
          activities: routingAnalysis.shouldModifyActivities ? '' : currentActivities, // Only clear activities if they will be modified
          isStreaming: true
        });
      } else {
        console.log('🚀 Phase 1: Skipping story modification (smart routing - not needed)');
        // If we're not modifying story, just mark activities as streaming
        handleSectionUpdate(activeTab, {
          activities: '',
          isStreaming: true
        });
      }

      try {
        let shouldModifyActivities: boolean = routingAnalysis.shouldModifyActivities;
        
        // Declare common variables at the top level for use in both story and activities modifications
        const currentParagraphCount = currentStory ? currentStory.split('\n\n').filter(p => p.trim()).length : 0;
        
        // Use section-specific paragraph count (for Poetry stanzas, this ensures independence between sections)
        const currentSectionParagraphCount = sectionParagraphCounts[activeTab] || currentParagraphCount;
        
        const convertedUserMessage = convertWordNumbersToDigits(userMessage.message);
        
        // STORY MODIFICATION PHASE (only if smart routing approves)
        if (routingAnalysis.shouldModifyStory) {
          // Enhanced paragraph tracking for modifications
          
          const requestPayload = {
            userRequest: convertedUserMessage, // Use converted message with digit numbers
            section: activeTab,
            currentStory: modifiedStoryContent || currentStory || '',
            currentActivities: currentActivities || '',
            lessonForm: {
              ...lessonPlan.form,
              lexileLevel: newSectionLexileLevel, // Use the updated section-specific Lexile level
              language: lessonPlan.form.language // Include language for content modification
            },
            // Enhanced paragraph tracking - section-specific
            currentParagraphCount: currentSectionParagraphCount,
            targetParagraphCount: sectionParagraphCounts[activeTab] || lessonPlan.form.paragraphCount, // Section-specific setting
            explicitParagraphInstructions: `The ${activeTab} story currently has ${currentSectionParagraphCount} ${lessonPlan.form.genre === 'Poetry' ? 'stanzas' : 'paragraphs'}. The user's request is: "${convertedUserMessage}". Please check if this request involves changing the number of ${lessonPlan.form.genre === 'Poetry' ? 'stanzas' : 'paragraphs'} (adding, removing, or setting a specific count). If so, modify the story accordingly and return the new ${lessonPlan.form.genre === 'Poetry' ? 'stanza' : 'paragraph'} count. IMPORTANT: This ${activeTab} section operates independently from other sections. Keep the story as pure narrative content - do not add questions, assessments, or educational activities to the story.`,
            // Keep original message for reference
            originalUserRequest: userMessage.message,
            // Smart routing context
            requestType: routingAnalysis.requestType,
            routingReasoning: routingAnalysis.reasoning,
            // Rigor analysis context
            rigorAnalysis: sectionSpecificRigorAnalysis,
            lexileLevelAdjusted,
            originalSectionLexileLevel,
            newSectionLexileLevel,
            // Poetry analysis context
            poetryAnalysis: routingAnalysis.poetryAnalysis
          };

          console.log('📊 Enhanced paragraph tracking for modification:', {
            currentParagraphCount,
            currentSectionParagraphCount,
            formParagraphCount: lessonPlan.form.paragraphCount,
            sectionParagraphCount: sectionParagraphCounts[activeTab],
            activeSection: activeTab,
            originalRequest: userMessage.message.substring(0, 100) + '...',
            convertedRequest: convertedUserMessage.substring(0, 100) + '...',
            conversionApplied: userMessage.message !== convertedUserMessage,
            payload: requestPayload
          });

          const storyResponse = await fetch(`${process.env.REACT_APP_AI_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:5001' : 'https://teach-ai-aq9x.vercel.app')}/small-group/modify-story`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestPayload),
            signal: abortController.signal
          });

          if (!storyResponse.ok) {
            throw new Error(`Story modification failed: ${storyResponse.status}`);
          }

          if (!storyResponse.body) {
            throw new Error('No response body available for story streaming');
          }

          // Process story streaming response
          const storyReader = storyResponse.body.getReader();
          let storyStreamContent = '';
          let isStoryComplete = false;

        while (!isStoryComplete) {
          const { done, value } = await storyReader.read();
          if (done) break;

          const textChunk = new TextDecoder().decode(value);
          const lines = textChunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                
                if (data.type === 'status') {
                  console.log('📊 Story modification status:', data);
                  console.log(`📊 Paragraph count: ${data.currentParagraphs} → ${data.targetParagraphs} (${data.paragraphChange >= 0 ? '+' : ''}${data.paragraphChange})`);
                  
                  // Enhanced status update with paragraph count information (Poetry-aware)
                  const contentUnit = lessonPlan.form.genre === 'Poetry' ? 'stanzas' : 'paragraphs';
                  const statusMessage = data.paragraphChange !== 0 
                    ? `📊 ${activeTab} section: ${data.currentParagraphs} → ${data.targetParagraphs} ${contentUnit} (${data.paragraphChange >= 0 ? '+' : ''}${data.paragraphChange})`
                    : `📊 ${activeTab} section: ${data.message}`;
                  
                  // Update UI with paragraph count information (simplified)
                  if (data.paragraphChange !== 0) {
                  setChatHistory(prev => [...prev, {
                    id: Date.now().toString(),
                      message: `${data.currentParagraphs} → ${data.targetParagraphs} ${contentUnit}`,
                    timestamp: new Date(),
                    type: 'system' as const
                  }]);
                  }
                } else if (data.type === 'route_to_activities') {
                  console.log('🔄 Story endpoint routing to activities only');
                  // PRESERVE STORY: When routing to activities-only, keep original story
                  modifiedStoryContent = currentStory; // Preserve original story content
                  shouldModifyActivities = true;
                  isStoryComplete = true;
                  break;
                } else if (data.type === 'content' && data.content) {
                  storyStreamContent += data.content;
                  
                  // Real-time story streaming display
                  if (storyStreamContent) {
                    handleStoryUpdate(activeTab, storyStreamContent);
                  }
                } else if (data.type === 'complete') {
                  if (data.title && data.paragraphs) {
                    const formattedStoryContent = data.paragraphs.join('\n\n');
                    modifiedStoryContent = formattedStoryContent;
                    handleStoryUpdate(activeTab, formattedStoryContent);
                    
                    console.log('✅ Story modification complete');
                    
                    // Enhanced paragraph count tracking - section-specific
                    const previousParagraphCount = data.previousParagraphCount || currentSectionParagraphCount;
                    const actualParagraphCount = data.paragraphCount || data.paragraphs.length;
                    const paragraphChange = actualParagraphCount - previousParagraphCount;
                    
                    console.log('📊 Enhanced paragraph count tracking (section-specific):', {
                      section: activeTab,
                      previous: previousParagraphCount,
                      target: data.targetParagraphCount || 'unknown',
                      actual: actualParagraphCount,
                      change: paragraphChange,
                      modificationType: data.modificationType || 'unknown',
                      userRequest: userMessage.message.substring(0, 50) + '...'
                    });
                    
                    // Update section-specific paragraph count (not the global form)
                    setSectionParagraphCounts(prev => {
                      const updated = {
                        ...prev,
                        [activeTab]: actualParagraphCount
                      };
                      console.log('📊 Updated section-specific paragraph count:', {
                        section: activeTab,
                        from: prev[activeTab],
                        to: actualParagraphCount,
                        change: actualParagraphCount - prev[activeTab],
                        allSections: updated
                      });
                      return updated;
                    });
                    
                    if (formattedStoryContent !== currentStory) {
                      hasStoryChanges = true;
                      appliedModifications.push('story');
                      console.log('✅ Story modified successfully');
                      
                      // Enhanced section-specific count change notification (Poetry-aware)
                      const contentUnit = lessonPlan.form.genre === 'Poetry' ? 'stanza' : 'paragraph';
                      const contentUnitPlural = lessonPlan.form.genre === 'Poetry' ? 'stanzas' : 'paragraphs';
                      
                      if (paragraphChange !== 0) {
                        setChatHistory(prev => [...prev, {
                          id: Date.now().toString(),
                          message: `Story updated: ${previousParagraphCount} → ${actualParagraphCount} ${actualParagraphCount > 1 ? contentUnitPlural : contentUnit}`,
                          timestamp: new Date(),
                          type: 'system' as const
                        }]);
                      } else {
                        setChatHistory(prev => [...prev, {
                          id: Date.now().toString(),
                          message: `Story modified`,
                          timestamp: new Date(),
                          type: 'system' as const
                        }]);
                      }
                    }
                  }
                  shouldModifyActivities = true;
                  isStoryComplete = true;
                  break;
                } else if (data.type === 'error') {
                  throw new Error(data.error || 'Story modification error');
                }
              } catch (parseError) {
                console.warn('Failed to parse story streaming data:', parseError);
              }
            }
          }
        }
        } // End of story modification if block

        // PHASE 2: Activities Modification (if needed based on smart routing)
        if (shouldModifyActivities) {
          console.log('🚀 Phase 2: Attempting activities modification');
          
          // Enhanced activities request with updated paragraph information
          const updatedParagraphCount = modifiedStoryContent ? modifiedStoryContent.split('\n\n').filter(p => p.trim()).length : currentParagraphCount;
          const activitiesRequestPayload = {
            userRequest: convertedUserMessage, // Use converted message for consistency
            section: activeTab,
            storyContent: modifiedStoryContent || '', // Use updated story content
            currentActivities: currentActivities || '',
            lessonForm: {
              ...lessonPlan.form,
              paragraphCount: updatedParagraphCount, // Use the updated paragraph count from story modification
              lexileLevel: newSectionLexileLevel, // Use the updated section-specific Lexile level for activities
              language: lessonPlan.form.language // Include language for content modification
            },
            // Additional context for activities modification
            storyParagraphCount: updatedParagraphCount,
            explicitInstructions: `The story now has ${updatedParagraphCount} paragraphs. The user's request was: "${convertedUserMessage}". Please modify the activities to align with any paragraph count changes or content modifications.${lexileLevelAdjusted ? ` IMPORTANT: The Lexile level has been automatically increased from ${originalSectionLexileLevel} to ${newSectionLexileLevel} due to increased rigor (${sectionSpecificRigorAnalysis.rigorType}). Generate activities at the ${newSectionLexileLevel} Lexile level to match the increased complexity.` : ''}`,
            // Keep original message for reference
            originalUserRequest: userMessage.message,
            // Rigor analysis context
            rigorAnalysis: sectionSpecificRigorAnalysis,
            lexileLevelAdjusted,
            originalSectionLexileLevel,
            newSectionLexileLevel
          };

          console.log('📊 Activities modification with updated paragraph context:', {
            originalParagraphCount: lessonPlan.form.paragraphCount,
            updatedParagraphCount,
            userRequest: userMessage.message.substring(0, 100) + '...'
          });
          
          const activitiesResponse = await fetch(`${process.env.REACT_APP_AI_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:5001' : 'https://teach-ai-aq9x.vercel.app')}/small-group/modify-activities`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(activitiesRequestPayload),
            signal: abortController.signal
          });

          if (!activitiesResponse.ok) {
            throw new Error(`Activities modification failed: ${activitiesResponse.status}`);
          }

          if (!activitiesResponse.body) {
            throw new Error('No response body available for activities streaming');
          }

          // Process activities streaming response
          const activitiesReader = activitiesResponse.body.getReader();
          let activitiesStreamContent = '';
          let isActivitiesComplete = false;

          while (!isActivitiesComplete) {
            const { done, value } = await activitiesReader.read();
            if (done) break;

            const textChunk = new TextDecoder().decode(value);
            const lines = textChunk.split('\n');

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));
                  
                  if (data.type === 'content' && data.content) {
                    activitiesStreamContent += data.content;
                    
                    // Real-time activities streaming display
                    handleActivitiesUpdate(activeTab, activitiesStreamContent);
                  } else if (data.type === 'complete') {
                    if (data.activities) {
                      modifiedActivitiesContent = data.activities;
                      handleActivitiesUpdate(activeTab, data.activities);
                      
                      if (data.activities !== currentActivities) {
                        hasActivitiesChanges = true;
                        appliedModifications.push('activities');
                        console.log('✅ Activities modified successfully');
                      }
                    }
                    
                    // REPOPULATE STORY: If activities endpoint returns story content, use it to repopulate
                    if (data.storyContent && data.storyContent.trim()) {
                      console.log('📖 Repopulating story from activities endpoint');
                      handleStoryUpdate(activeTab, data.storyContent);
                      modifiedStoryContent = data.storyContent;
                    }
                    
                    isActivitiesComplete = true;
                    break;
                  } else if (data.type === 'error') {
                    throw new Error(data.error || 'Activities modification error');
                  }
                } catch (parseError) {
                  console.warn('Failed to parse activities streaming data:', parseError);
                }
              }
            }
          }
        }

      } catch (modificationError) {
        console.error('Modification error:', modificationError);
        throw modificationError;
      }

      // Clear streaming state
      handleSectionUpdate(activeTab, {
        isStreaming: false
      });

      // Provide user feedback
      const hasChanges = hasStoryChanges || hasActivitiesChanges;
      const successMessage = {
        id: (Date.now() + 1).toString(),
        message: hasChanges 
          ? `Modified ${appliedModifications.join(' and ')}`
          : "No changes needed",
        timestamp: new Date(),
        type: 'system' as const
      };

      // Use a longer delay to ensure state updates have fully propagated
      setTimeout(() => {
        setChatHistory(prev => [...prev, successMessage]);
        setIsChatProcessing(false);
        
        // Clear backup after successful modification
        setContentBackup(null);
        console.log('🗑️ Cleared content backup after successful modification');
        
        console.log('✅ Sequential modification cycle complete:', {
          modificationsApplied: appliedModifications,
          hasChanges,
          processingComplete: true
        });
        
        // Schedule translation regeneration after state has settled
        // This runs after the state updates from handleStoryUpdate/handleActivitiesUpdate
        setTimeout(() => {
          console.log('🌐 Triggering translation regeneration with updated content');
          // Check if the lesson currently has any translations
          const introSec = lessonPlan.sections.introduction;
          const indepSec = lessonPlan.sections.independent;
          const hasTranslationsIntro = !!(introSec.storySpanish?.content || introSec.activitiesSpanish);
          const hasTranslationsIndependent = !!(indepSec.storySpanish?.content || indepSec.activitiesSpanish);
          const hasAnyTranslations = hasTranslationsIntro || hasTranslationsIndependent;

          if (hasAnyTranslations) {
            // Re-translate Introduction if translations exist (or lesson needs Spanish).
            // If this was the modified section, pass explicit retranslate flags based on what changed.
            regenerateSectionTranslations(
              'introduction',
              {
                retranslateStory: activeTab === 'introduction' ? hasStoryChanges : undefined,
                retranslateActivities: activeTab === 'introduction' ? hasActivitiesChanges : undefined
              },
              activeTab === 'introduction'
                ? { storyContent: hasStoryChanges ? (modifiedStoryContent || undefined) : undefined,
                    activitiesContent: hasActivitiesChanges ? (modifiedActivitiesContent || undefined) : undefined }
                : undefined
            );

            // Re-translate Independent Practice similarly
            regenerateSectionTranslations(
              'independent',
              {
                retranslateStory: activeTab === 'independent' ? hasStoryChanges : undefined,
                retranslateActivities: activeTab === 'independent' ? hasActivitiesChanges : undefined
              },
              activeTab === 'independent'
                ? { storyContent: hasStoryChanges ? (modifiedStoryContent || undefined) : undefined,
                    activitiesContent: hasActivitiesChanges ? (modifiedActivitiesContent || undefined) : undefined }
                : undefined
            );
          } else {
            // Fallback: only translate the modified section using explicit flags
            regenerateSectionTranslations(
              activeTab,
              {
                retranslateStory: hasStoryChanges,
                retranslateActivities: hasActivitiesChanges
              },
              { storyContent: hasStoryChanges ? (modifiedStoryContent || undefined) : undefined,
                activitiesContent: hasActivitiesChanges ? (modifiedActivitiesContent || undefined) : undefined }
            );
          }
        }, 200); // Additional delay to ensure React state batch updates are complete
      }, 100);

    } catch (error) {
      console.error('Chat error:', error);
      
      // Clean up abort controller
      setChatAbortController(null);
      
      // Clean up streaming state on error
      handleSectionUpdate(activeTab, {
        isStreaming: false
      });
      
      // Handle abort error specifically (user clicked Stop button)
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('Chat processing was cancelled by user');
        // Note: contentBackup will be restored by handleCancelChat
        return; // Don't show error message for user-initiated cancellation
      }
      
      // For non-abort errors, also restore the backup
      if (contentBackup) {
        console.log('🔄 Restoring content from backup after error');
        
        // Clear translation regenerating flag
        setTranslationsRegenerating(prev => ({ ...prev, [contentBackup.section]: false }));
        
        if (contentBackup.story !== null) {
          handleStoryUpdate(contentBackup.section, contentBackup.story);
        }
        
        if (contentBackup.activities !== null) {
          handleActivitiesUpdate(contentBackup.section, contentBackup.activities);
        }
        
        setContentBackup(null);
      }
      
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: new Date(),
        type: 'system' as const
      };
      
      setTimeout(() => {
        setChatHistory(prev => [...prev, errorMessage]);
        setIsChatProcessing(false);
      }, 1000);
    } finally {
      // Always clean up abort controller
      setChatAbortController(null);
    }
  }, [chatMessage, activeTab, lessonPlan.sections, lessonPlan.form, handleStoryUpdate, handleActivitiesUpdate, handleSectionUpdate, contentBackup]);

  // SIMPLIFIED PARSING: Handle the new simplified format from backend
  const parseModifiedContent = (modifiedContent: string, originalStory: string | null, originalActivities: string | null, userMessage = '') => {
    console.log('🚀 SIMPLIFIED PARSING:', { 
      contentLength: modifiedContent.length,
      hasOriginalStory: !!originalStory, 
      hasOriginalActivities: !!originalActivities,
      userMessage: userMessage.substring(0, 50) + '...'
    });

    // Look for the new simplified format
    if (modifiedContent.includes('=== STORY CONTENT ===') && modifiedContent.includes('=== ACTIVITIES CONTENT ===')) {
      console.log('✅ Found simplified format separators');
      
      // Extract story content (everything between story separator and activities separator)
      const storyMatch = modifiedContent.match(/=== STORY CONTENT ===\s*([\s\S]*?)(?=== ACTIVITIES CONTENT ===)/);
      // Extract activities content (everything after activities separator)
      const activitiesMatch = modifiedContent.match(/=== ACTIVITIES CONTENT ===\s*([\s\S]*?)$/);
      
      let storyContent = originalStory;
      let activitiesContent = originalActivities;
      
      if (storyMatch && storyMatch[1].trim()) {
        storyContent = storyMatch[1].trim();
        console.log('✅ Successfully extracted story content:', storyContent.substring(0, 100) + '...');
      }
      
      if (activitiesMatch && activitiesMatch[1].trim()) {
        activitiesContent = activitiesMatch[1].trim();
        console.log('✅ Successfully extracted activities content:', activitiesContent.substring(0, 100) + '...');
      }
      
        return { storyContent, activitiesContent };
    }

    // FALLBACK: If new format not found, assume it's all activity content (common case)
    console.log('⚠️ Simplified format not found, treating as activities content');
    return { 
      storyContent: originalStory, 
      activitiesContent: modifiedContent.trim() || originalActivities 
    };
  };

  /**
   * Checks if there's content available for download
   * @returns boolean indicating if lesson plan has downloadable content
   */
  const hasDownloadableContent = () => {
    const introSection = lessonPlan.sections.introduction;
    const independentSection = lessonPlan.sections.independent;
    
    // Check for any content in either section (including translated content)
    const hasIntroContent = !!(
      introSection.story?.content || 
      introSection.activities ||
      introSection.storySpanish?.content ||
      introSection.activitiesSpanish ||
      introSection.isGenerated
    );
    
    const hasIndependentContent = !!(
      independentSection.story?.content || 
      independentSection.activities ||
      independentSection.storySpanish?.content ||
      independentSection.activitiesSpanish ||
      independentSection.isGenerated
    );
    
    console.log('Download check:', {
      introSection,
      independentSection,
      hasIntroContent,
      hasIndependentContent,
      result: hasIntroContent || hasIndependentContent
    });
    
    return hasIntroContent || hasIndependentContent;
  };

  /**
   * Formats markdown content by removing markdown syntax for clean document output
   * @param content - The markdown content to format
   * @returns Formatted content without markdown syntax
   */
  const formatMarkdownContent = (content: string) => {
    if (!content) return '';
    
    return content
      .replace(/&quot;/g, '"') // Fix encoded quotes
      .replace(/&#x27;/g, "'") // Fix encoded apostrophes
      .replace(/&amp;/g, '&') // Fix encoded ampersands
      .replace(/&lt;/g, '<') // Fix encoded less than
      .replace(/&gt;/g, '>') // Fix encoded greater than
      // Fix UTF-8 smart quotes and special characters
      .replace(/â€™/g, "'")  // Fix mangled apostrophe
      .replace(/â€œ/g, '"')  // Fix mangled opening quote
      .replace(/â€/g, '"')   // Fix mangled closing quote
      .replace(/â€"/g, '-')  // Fix mangled dash
      .replace(/â€¦/g, '...') // Fix mangled ellipsis
      .replace(/Â/g, '')     // Remove stray Â characters
      .replace(/'/g, "'")    // Smart apostrophe to regular
      .replace(/'/g, "'")    // Smart apostrophe to regular
      .replace(/"/g, '"')    // Smart quote to regular
      .replace(/"/g, '"')    // Smart quote to regular
      .replace(/–/g, '-')    // En dash to regular dash
      .replace(/—/g, '-')    // Em dash to regular dash
      .replace(/\*\*/g, '') // Remove bold markers
      .replace(/\*/g, '')   // Remove italic markers
      .replace(/#{1,6}\s/g, '') // Remove heading markers
      .replace(/`/g, '')    // Remove code markers
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Replace links with just text
      // Handle RTF special characters
      .replace(/\\/g, '\\\\')
      .replace(/\{/g, '\\{')
      .replace(/\}/g, '\\}')
      // Keep apostrophes and quotes as regular characters for RTF
      .replace(/\n\s*\n/g, '\\par\\par ') // Convert double newlines to RTF paragraphs
      .replace(/\n/g, '\\line ') // Convert single newlines to RTF line breaks
      .replace(/\s+/g, ' ') // Normalize spaces
      .trim();
  };

  /**
   * Formats content for HTML output by converting markdown to HTML with proper paragraphs
   * @param content - The markdown content to format
   * @returns HTML formatted content with proper paragraph structure
   */
  const formatHtmlContent = (content: string) => {
    if (!content) return '';
    
    // First clean up encoding issues
    let cleanContent = content
      .replace(/&quot;/g, '"') // Fix encoded quotes
      .replace(/&#x27;/g, "'") // Fix encoded apostrophes
      .replace(/&amp;/g, '&') // Fix encoded ampersands
      .replace(/&lt;/g, '<') // Fix encoded less than
      .replace(/&gt;/g, '>') // Fix encoded greater than
      // Fix UTF-8 smart quotes and special characters
      .replace(/â€™/g, "'")  // Fix mangled apostrophe
      .replace(/â€œ/g, '"')  // Fix mangled opening quote
      .replace(/â€/g, '"')   // Fix mangled closing quote
      .replace(/â€"/g, '-')  // Fix mangled dash
      .replace(/â€¦/g, '...') // Fix mangled ellipsis
      .replace(/Â/g, '')     // Remove stray Â characters
      .replace(/'/g, "'")    // Smart apostrophe to regular
      .replace(/'/g, "'")    // Smart apostrophe to regular
      .replace(/"/g, '"')    // Smart quote to regular
      .replace(/"/g, '"')    // Smart quote to regular
      .replace(/–/g, '-')    // En dash to regular dash
      .replace(/—/g, '-')    // Em dash to regular dash
      .replace(/\*\*/g, '') // Remove bold markers
      .replace(/\*/g, '')   // Remove italic markers
      .replace(/`/g, '')    // Remove code markers
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Replace links with just text
      .replace(/---+/g, '') // Remove triple dashes and more
      .replace(/#{1,6}\s*/g, ''); // Remove markdown heading markers
    
    // Process line by line to maintain structure like in the UI
    const lines = cleanContent.split('\n').filter(line => line.trim());
    let formattedContent = '';
    let inList = false;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      if (!line) continue;
      
      // Check for section headers with time (like "Pre-Reading Activities (10-15 minutes)")
      if (line.match(/.*\(\d+-\d+\s*minutes?\)/) && !line.startsWith('-') && !line.match(/^\d+\./)) {
        if (inList) {
          formattedContent += '</ul>';
          inList = false;
        }
        const timeMatch = line.match(/\((\d+-\d+\s*minutes?)\)/);
        const sectionTitle = line.replace(/\s*\(\d+-\d+\s*minutes?\)/, '').trim();
        formattedContent += `<h3 style="color: #1976d2; font-weight: bold; font-size: 16px; margin: 20px 0 10px 0; border-bottom: 2px solid #1976d2; padding-bottom: 4px;">${sectionTitle}</h3>`;
        if (timeMatch) {
          formattedContent += `<p style="color: #666; font-style: italic; margin: 5px 0 15px 0;">(${timeMatch[1]})</p>`;
        }
        continue;
      }
      
      // Check for numbered activity headers (like "1. Pre-Reading Discussion (2-3 minutes)")
      if (line.match(/^\d+\.\s+/)) {
        if (inList) {
          formattedContent += '</ul>';
          inList = false;
        }
        const activityTitle = line.replace(/^\d+\.\s+/, '');
        const timeMatch = activityTitle.match(/\((\d+-\d+\s*minutes?)\)/);
        const title = activityTitle.replace(/\s*\(\d+-\d+\s*minutes?\)/, '').trim();
        const numberMatch = line.match(/^\d+\./);
        
        formattedContent += `<h4 style="color: #495057; font-weight: bold; font-size: 14px; margin: 15px 0 8px 0; padding: 4px 0; border-bottom: 1px solid #dee2e6;">${numberMatch ? numberMatch[0] : ''} ${title}</h4>`;
        if (timeMatch) {
          formattedContent += `<p style="color: #666; font-style: italic; margin: 5px 0 10px 0;">(${timeMatch[1]})</p>`;
        }
        continue;
      }
      
      // Check for special instruction types
      if (line.match(/^([A-Z][^:]*Instructions):\s*/)) {
        if (inList) {
          formattedContent += '</ul>';
          inList = false;
        }
        const match = line.match(/^([A-Z][^:]*Instructions):\s*(.*)/);
        if (match) {
          formattedContent += `<h4 style="background-color: #fff3cd; color: #856404; font-weight: bold; font-size: 14px; margin: 15px 0 8px 0; padding: 8px 12px; border-left: 4px solid #ffc107; border-radius: 4px;">${match[1]}:</h4>`;
          if (match[2]) {
            formattedContent += `<p style="margin-bottom: 12px; line-height: 1.6;">${match[2]}</p>`;
          }
        }
        continue;
      }
      
      if (line.match(/^([A-Z][^:]*Expected Responses?):\s*/)) {
        if (inList) {
          formattedContent += '</ul>';
          inList = false;
        }
        const match = line.match(/^([A-Z][^:]*Expected Responses?):\s*(.*)/);
        if (match) {
          formattedContent += `<h4 style="background-color: #d1ecf1; color: #0c5460; font-weight: bold; font-size: 14px; margin: 15px 0 8px 0; padding: 8px 12px; border-left: 4px solid #17a2b8; border-radius: 4px;">${match[1]}:</h4>`;
          if (match[2]) {
            formattedContent += `<p style="margin-bottom: 12px; line-height: 1.6;">${match[2]}</p>`;
          }
        }
        continue;
      }
      
      if (line.match(/^([A-Z][^:]*Vocabulary Words?):\s*/)) {
        if (inList) {
          formattedContent += '</ul>';
          inList = false;
        }
        const match = line.match(/^([A-Z][^:]*Vocabulary Words?):\s*(.*)/);
        if (match) {
          formattedContent += `<h4 style="background-color: #d4edda; color: #155724; font-weight: bold; font-size: 14px; margin: 15px 0 8px 0; padding: 8px 12px; border-left: 4px solid #28a745; border-radius: 4px;">${match[1]}:</h4>`;
          if (match[2]) {
            formattedContent += `<p style="margin-bottom: 12px; line-height: 1.6;">${match[2]}</p>`;
          }
        }
        continue;
      }
      
      // Check for bullet points (this is the key fix!)
      if (line.startsWith('- ')) {
        const bulletText = line.substring(2).trim();
        
        if (!inList) {
          formattedContent += '<ul style="margin: 10px 0; padding-left: 20px;">';
          inList = true;
        }
        
        // Check if it has a label (like "Words to explore:")
        const colonIndex = bulletText.indexOf(':');
        if (colonIndex > 0 && colonIndex < 50) {
          const label = bulletText.substring(0, colonIndex).trim();
          const text = bulletText.substring(colonIndex + 1).trim();
          formattedContent += `<li style="margin-bottom: 8px; line-height: 1.6;"><strong>${label}:</strong> ${text}</li>`;
        } else {
          formattedContent += `<li style="margin-bottom: 8px; line-height: 1.6;">${bulletText}</li>`;
        }
        continue;
      }
      
      // Check for standalone labels (ends with colon)
      if (line.endsWith(':') && line.length < 100) {
        if (inList) {
          formattedContent += '</ul>';
          inList = false;
        }
        formattedContent += `<h4 style="color: #495057; font-weight: bold; font-size: 14px; margin: 15px 0 8px 0; padding: 4px 0; border-bottom: 1px solid #dee2e6;">${line}</h4>`;
        continue;
      }
      
      // Regular paragraph text
      if (inList) {
        formattedContent += '</ul>';
        inList = false;
      }
      formattedContent += `<p style="margin-bottom: 12px; line-height: 1.6;">${line}</p>`;
    }
    
    // Close any open list
    if (inList) {
      formattedContent += '</ul>';
    }
    
    return formattedContent;
  };

  // Removes "Título:" or "Title:" prefix from a title string
  const stripTitlePrefix = (title: string): string => {
    if (!title) return '';
    const prefixRegex = /^\s*(?:title|t[íi]tulo)\s*:\s*/i;
    return title.replace(prefixRegex, '').trim();
  };

  // Removes a leading title line from content like "Title: ..." or "Título: ..."
  const stripLeadingTitleLine = (text: string): string => {
    if (!text) return text;
    
    let result = text.trim();
    
    // Multiple regex patterns to catch different formats
    const patterns = [
      // Pattern 1: "Título: ..." or "Title: ..." at start of line
      /^\s*(?:title|t[íi]tulo)\s*:\s*.*$/im,
      // Pattern 2: HTML wrapped version "<p>Título: ...</p>"
      /^\s*<p>\s*(?:title|t[íi]tulo)\s*:\s*.*?<\/p>\s*/im,
      // Pattern 3: Just the title line by itself (no colon)
      /^\s*(?:title|t[íi]tulo)\s*.*$/im,
      // Pattern 4: Any line that starts with "Título:" or "Title:" followed by content
      /^\s*(?:title|t[íi]tulo)\s*:\s*[^\n]*\n?/im
    ];
    
    // Apply each pattern until no more matches
    let changed = true;
    while (changed) {
      changed = false;
      for (const pattern of patterns) {
        if (pattern.test(result)) {
          result = result.replace(pattern, '').trim();
          changed = true;
        }
      }
    }
    
    return result;
  };

  /**
   * Formats story content with proper paragraph structure - preserves original paragraphs
   * Applies genre-specific formatting for poetry, drama, and other content types
   * @param content - The story content to format
   * @param genre - The genre of the content (for special formatting)
   * @returns HTML formatted story content
   */
  const formatStoryContent = (content: string, genre?: string) => {
    if (!content) return '';
    
    // Clean up encoding issues first
    let cleanContent = content
      .replace(/&quot;/g, '"')
      .replace(/&#x27;/g, "'")
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      // Fix UTF-8 smart quotes and special characters
      .replace(/â€™/g, "'")  // Fix mangled apostrophe
      .replace(/â€œ/g, '"')  // Fix mangled opening quote
      .replace(/â€/g, '"')   // Fix mangled closing quote
      .replace(/â€"/g, '-')  // Fix mangled dash
      .replace(/â€¦/g, '...') // Fix mangled ellipsis
      .replace(/Â/g, '')     // Remove stray Â characters
      .replace(/'/g, "'")    // Smart apostrophe to regular
      .replace(/'/g, "'")    // Smart apostrophe to regular
      .replace(/"/g, '"')    // Smart quote to regular
      .replace(/"/g, '"')    // Smart quote to regular
      .replace(/–/g, '-')    // En dash to regular dash
      .replace(/—/g, '-');   // Em dash to regular dash

    // Remove duplicated embedded title line if present
    cleanContent = stripLeadingTitleLine(cleanContent);
    
    // Handle Poetry genre with special stanza formatting
    if (genre === 'Poetry') {
      const stanzas = cleanContent
        .split(/\n\s*\n/)
        .filter((stanza: string) => stanza.trim());
      
      return stanzas
        .map((stanza: string, stanzaIndex: number) => {
          const lines = stanza.split('\n')
            .map((line: string) => line.trim())
            .filter((line: string) => line);
          
          const stanzaHtml = lines
            .map((line: string) => `<div style="text-align: center; margin-bottom: 4px; font-style: italic; line-height: 1.6;">${line}</div>`)
            .join('');
          
          return `<div style="margin-bottom: 24px; page-break-inside: avoid;">${stanzaHtml}</div>`;
        })
        .join('');
    }
    
    // Handle Drama genre with character names and stage directions
    if (genre === 'Drama' && (cleanContent.includes('**') || cleanContent.includes('(Characters):'))) {
      const lines = cleanContent.split('\n').filter((line: string) => line.trim());
      
      return lines
        .map((line: string) => {
          const trimmedLine = line.trim();
          
          // Handle character names in dialogue (bold formatting)
          if (/\*\*[A-Z][^*]*\*\*:/.test(trimmedLine) || /[A-Z][^:]*\*\*:/.test(trimmedLine)) {
            const processedLine = trimmedLine
              .replace(/\*\*([^*]+)\*\*:/g, '<strong>$1</strong>:')
              .replace(/([A-Z][A-Z\s.]+)\*\*:/g, '<strong>$1</strong>:');
            return `<div style="margin-bottom: 8px; line-height: 1.6; page-break-inside: avoid;">${processedLine}</div>`;
          }
          
          // Handle stage directions and other content
          return `<div style="margin-bottom: 8px; line-height: 1.6; page-break-inside: avoid;">${trimmedLine}</div>`;
        })
        .join('');
    }
    
    // Default formatting for other genres
    const paragraphs = cleanContent
      .split(/\n\s*\n/)
      .filter((paragraph: string) => paragraph.trim())
      .map((paragraph: string) => paragraph.trim().replace(/\n/g, ' '));
    
    return paragraphs
      .map((paragraph: string) => `<p style="margin-bottom: 16px; line-height: 1.8; text-indent: 20px; page-break-inside: avoid; orphans: 3; widows: 3;">${paragraph}</p>`)
      .join('');
  };



  /**
   * Handles opening the download menu
   * @param event - The click event
   */
  const handleDownloadClick = (event: React.MouseEvent<HTMLElement>) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  /**
   * Handles closing the download menu
   */
  const handleDownloadClose = () => {
    setDownloadAnchorEl(null);
  };

  /**
   * Generates and downloads the lesson plan as a PDF file
   * Uses html2pdf library to convert HTML content to PDF
   */
  const handleDownloadPDF = async () => {
    handleDownloadClose();
    
    if (!hasDownloadableContent()) {
      setSnackbar({
        open: true,
        message: 'No content available to download. Please generate lesson content first.',
        severity: 'warning'
      });
      return;
    }

    const introSection = lessonPlan.sections.introduction;
    const independentSection = lessonPlan.sections.independent;
    const standardInfo = selectedStandard;
    
    const element = `
      <div style="font-family: Arial, sans-serif;">
        ${introSection.story?.content ? `
          <div class="section">
            <h2>Introduction Story: ${stripTitlePrefix(introSection.story.title)}</h2>
            <div class="story-content">${formatStoryContent(introSection.story.content, lessonPlan.form.genre)}</div>
          </div>
        ` : ''}

        ${introSection.activities ? `
          <div class="section">
            <h2>Introduction Activities</h2>
            <div class="activities-content">${formatHtmlContent(introSection.activities)}</div>
          </div>
        ` : ''}

        ${introSection.storySpanish?.content ? `
          <div class="section">
            <h2>Introduction Story (Spanish): ${stripTitlePrefix(introSection.storySpanish.title)}</h2>
            <div class="story-content">${formatStoryContent(introSection.storySpanish.content, lessonPlan.form.genre)}</div>
          </div>
        ` : ''}

        ${introSection.activitiesSpanish ? `
          <div class="section">
            <h2>Introduction Activities (Spanish)</h2>
            <div class="activities-content">${formatHtmlContent(introSection.activitiesSpanish)}</div>
          </div>
        ` : ''}

        ${independentSection.story?.content ? `
          <div class="section">
            <h2>Independent Practice Story: ${stripTitlePrefix(independentSection.story.title)}</h2>
            <div class="story-content">${formatStoryContent(independentSection.story.content, lessonPlan.form.genre)}</div>
          </div>
        ` : ''}

        ${independentSection.activities ? `
          <div class="section">
            <h2>Independent Practice Activities</h2>
            <div class="activities-content">${formatHtmlContent(independentSection.activities)}</div>
          </div>
        ` : ''}

        ${independentSection.storySpanish?.content ? `
          <div class="section">
            <h2>Independent Practice Story (Spanish): ${stripTitlePrefix(independentSection.storySpanish.title)}</h2>
            <div class="story-content">${formatStoryContent(independentSection.storySpanish.content, lessonPlan.form.genre)}</div>
          </div>
        ` : ''}

        ${independentSection.activitiesSpanish ? `
          <div class="section">
            <h2>Independent Practice Activities (Spanish)</h2>
            <div class="activities-content">${formatHtmlContent(independentSection.activitiesSpanish)}</div>
          </div>
        ` : ''}
      </div>
    `;

    const opt = {
      margin: 0.75,
      filename: `small-group-lesson-plan-${lessonPlan.form.topic || 'lesson'}${lessonPlan.form.independentPracticeTopic ? `-${lessonPlan.form.independentPracticeTopic}` : ''}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        letterRendering: true
      },
      jsPDF: { 
        unit: 'in', 
        format: 'letter', 
        orientation: 'portrait',
        compress: true
      },
      pagebreak: { 
        mode: ['avoid-all'],
        before: '.section',
        avoid: ['h2', '.content-wrapper', '.content']
      }
    };

    try {
      await html2pdf().set(opt).from(element).save();
      setSnackbar({
        open: true,
        message: 'PDF downloaded successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      setSnackbar({
        open: true,
        message: 'Failed to generate PDF. Please try again.',
        severity: 'error'
      });
    }
  };

  /**
   * Generates and downloads the lesson plan as a Word document
   * Uses HTML format with Microsoft Word XML namespaces for better compatibility
   */
  const handleDownloadWord = () => {
    handleDownloadClose();
    
    if (!hasDownloadableContent()) {
      setSnackbar({
        open: true,
        message: 'No content available to download. Please generate lesson content first.',
        severity: 'warning'
      });
      return;
    }

    try {
      const introSection = lessonPlan.sections.introduction;
      const independentSection = lessonPlan.sections.independent;
      const standardInfo = selectedStandard;
      const storyFontPx = getStoryFontSizePx();
      const storyFontStyle = storyFontPx ? `font-size: ${storyFontPx}px;` : '';
      
      const wordContent = `
        <html xmlns:w="urn:schemas-microsoft-com:office:word"
              xmlns:v="urn:schemas-microsoft-com:vml"
              xmlns:o="urn:schemas-microsoft-com:office:office">
          <head>
            <meta charset="utf-8">
            <xml>
              <w:WordDocument>
                <w:View>Print</w:View>
                <w:Zoom>100</w:Zoom>
                <w:DoNotOptimizeForBrowser/>
                <w:BrowserLevel>MicrosoftInternetExplorer4</w:BrowserLevel>
              </w:WordDocument>
            </xml>
            <style>
              /* Document settings */
              @page Section1 {
                size: 8.5in 11.0in;
                margin: 1.0in;
                mso-header-margin: .5in;
                mso-footer-margin: .5in;
                mso-paper-source: 0;
              }

              div.WordSection1 {
                page: Section1;
              }

              /* Basic styles matching print format */
              body {
                font-family: Arial, sans-serif;
                font-size: 12pt;
                line-height: 1.6;
                margin: 0;
                padding: 20px;
              }

              h1 {
                text-align: center;
                font-size: 20pt;
                font-weight: bold;
                color: #1976d2;
                margin: 0 0 24pt 0;
                padding: 12pt 0;
                border-bottom: 2pt solid #1976d2;
              }

              h2 {
                font-size: 16pt;
                font-weight: bold;
                color: #1976d2;
                margin: 24pt 0 12pt 0;
                page-break-after: avoid;
              }

              .lesson-info {
                background-color: #f5f5f5;
                padding: 12pt;
                margin: 12pt 0;
                border-radius: 4pt;
              }

              .lesson-info div {
                margin-bottom: 6pt;
              }

              .lesson-info strong {
                color: #1976d2;
              }

              .section {
                margin-bottom: 24pt;
                page-break-inside: avoid;
              }

              .story-content {
                margin: 12pt 0;
                white-space: pre-wrap;
                word-wrap: break-word;
                ${storyFontStyle}
              }

              .activities-content {
                margin: 12pt 0;
              }

              .activities-content h3 {
                color: #1976d2;
                font-weight: bold;
                font-size: 14pt;
                margin: 18pt 0 9pt 0;
                border-bottom: 1pt solid #1976d2;
                padding-bottom: 3pt;
              }

              .activities-content h4 {
                font-weight: bold;
                font-size: 12pt;
                margin: 12pt 0 6pt 0;
              }

              .activities-content p {
                margin-bottom: 12pt;
                line-height: 1.7;
              }

              .activities-content ul, 
              .activities-content ol {
                padding-left: 24pt;
                margin-bottom: 12pt;
              }

              .activities-content li {
                margin-bottom: 6pt;
                line-height: 1.6;
              }

              p {
                margin: 12pt 0;
                orphans: 4;
                widows: 4;
              }

              strong {
                font-weight: 600;
                color: #333;
              }

              em {
                font-style: italic;
                color: #666;
              }
            </style>
          </head>
          <body>
            <div class="WordSection1">
              <h1>Lesson Plan</h1>
              
              ${standardInfo ? `
                <div style="margin-bottom: 24pt;">
                  <strong>Standard:</strong> ${standardInfo.standard || standardInfo.code}
                  ${standardInfo.description ? `<br><em>${capitalizeFirstLetter(standardInfo.description)}</em>` : ''}
                </div>
              ` : ''}

              <div class="lesson-info">
                <div><strong>Topic:</strong> ${lessonPlan.form.topic}</div>
                <div><strong>Genre:</strong> ${lessonPlan.form.genre}</div>
                <div><strong>Lexile Level:</strong> ${lessonPlan.form.lexileLevel}</div>
                <div><strong>Paragraphs:</strong> ${lessonPlan.form.paragraphCount}</div>
                <div><strong>Language:</strong> ${languages.find(lang => lang.value === lessonPlan.form.language)?.label || 'English'}</div>
              </div>

              ${introSection.story?.content ? `
                <div class="section">
                  <h2>Introduction Story: ${stripTitlePrefix(introSection.story.title)}</h2>
                  <div class="story-content">${formatStoryContent(introSection.story.content, lessonPlan.form.genre)}</div>
                </div>
              ` : ''}

              ${introSection.activities ? `
                <div class="section">
                  <h2>Introduction Activities</h2>
                  <div class="activities-content">${formatHtmlContent(introSection.activities)}</div>
                </div>
              ` : ''}

              ${introSection.storySpanish?.content ? `
                <div class="section">
                  <h2>Introduction Story (Spanish): ${stripTitlePrefix(introSection.storySpanish.title)}</h2>
                  <div class="story-content">${formatStoryContent(introSection.storySpanish.content, lessonPlan.form.genre)}</div>
                </div>
              ` : ''}

              ${introSection.activitiesSpanish ? `
                <div class="section">
                  <h2>Introduction Activities (Spanish)</h2>
                  <div class="activities-content">${formatHtmlContent(introSection.activitiesSpanish)}</div>
                </div>
              ` : ''}

              ${independentSection.story?.content ? `
                <div class="section">
                  <h2>Independent Practice Story: ${stripTitlePrefix(independentSection.story.title)}</h2>
                  <div class="story-content">${formatStoryContent(independentSection.story.content, lessonPlan.form.genre)}</div>
                </div>
              ` : ''}

              ${independentSection.activities ? `
                <div class="section">
                  <h2>Independent Practice Activities</h2>
                  <div class="activities-content">${formatHtmlContent(independentSection.activities)}</div>
                </div>
              ` : ''}

              ${independentSection.storySpanish?.content ? `
                <div class="section">
                  <h2>Independent Practice Story (Spanish): ${stripTitlePrefix(independentSection.storySpanish.title)}</h2>
                  <div class="story-content">${formatStoryContent(independentSection.storySpanish.content, lessonPlan.form.genre)}</div>
                </div>
              ` : ''}

              ${independentSection.activitiesSpanish ? `
                <div class="section">
                  <h2>Independent Practice Activities (Spanish)</h2>
                  <div class="activities-content">${formatHtmlContent(independentSection.activitiesSpanish)}</div>
                </div>
              ` : ''}
            </div>
          </body>
        </html>
      `;

      const blob = new Blob([wordContent], { type: 'application/msword' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `small-group-lesson-plan-${lessonPlan.form.topic || 'lesson'}.doc`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      
      setSnackbar({
        open: true,
        message: 'Word document downloaded successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error generating Word document:', error);
      setSnackbar({
        open: true,
        message: 'Failed to generate Word document. Please try again.',
        severity: 'error'
      });
    }
  };

  /**
   * Handles printing the lesson plan
   * Creates a print-friendly version and opens print dialog
   */
  const handlePrint = () => {
    if (!hasDownloadableContent()) {
      setSnackbar({
        open: true,
        message: 'No content available to print. Please generate lesson content first.',
        severity: 'warning'
      });
      return;
    }

    const introSection = lessonPlan.sections.introduction;
    const independentSection = lessonPlan.sections.independent;
    const standardInfo = selectedStandard;
    
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setSnackbar({
        open: true,
        message: 'Unable to open print window. Please check your popup blocker.',
        severity: 'error'
      });
      return;
    }

    const storyFontPx = getStoryFontSizePx();
    const storyFontStyle = storyFontPx ? `font-size: ${storyFontPx}px;` : '';
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Lesson Plan - ${lessonPlan.form.topic}</title>
          <style>
            @page {
              margin: 1in;
              size: letter;
            }
            
            @media print {
              @page {
                margin: 1in;
                size: letter;
              }
            }

            body {
              font-family: Arial, sans-serif;
              line-height: 1.6;
              padding: 20px;
              max-width: 800px;
              margin: 0 auto;
            }

            h1, h2 {
              color: #1976d2;
              margin-top: 2em;
              margin-bottom: 1em;
            }

            h1 {
              text-align: center;
              margin-top: 0;
            }

            .lesson-info {
              background-color: #f5f5f5;
              padding: 1em;
              margin: 1em 0;
              border-radius: 4px;
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
              gap: 1em;
            }

            .story-content {
              padding: 1em;
              margin: 1em 0;
              background: #fff;
              border-radius: 4px;
              ${storyFontStyle}
            }

            .activities-content {
              padding: 1em;
              margin: 1em 0;
              background: #fff;
              border-radius: 4px;
            }

            .section {
              page-break-inside: avoid;
            }
          </style>
        </head>
        <body>
          <h1>Lesson Plan</h1>

          ${standardInfo ? `
            <div class="lesson-info">
              <div><strong>Standard:</strong> ${standardInfo.standard || standardInfo.code}</div>
              ${standardInfo.description ? `<div><em>${capitalizeFirstLetter(standardInfo.description)}</em></div>` : ''}
            </div>
          ` : ''}

          <div class="lesson-info">
            <div><strong>Topic:</strong> ${lessonPlan.form.topic}</div>
            <div><strong>Genre:</strong> ${lessonPlan.form.genre}</div>
            <div><strong>Lexile Level:</strong> ${lessonPlan.form.lexileLevel}</div>
            <div><strong>Paragraphs:</strong> ${lessonPlan.form.paragraphCount}</div>
            <div><strong>Language:</strong> ${languages.find(lang => lang.value === lessonPlan.form.language)?.label || 'English'}</div>
          </div>

          ${introSection.story?.content ? `
            <div class="section">
              <h2>Introduction Story: ${stripTitlePrefix(introSection.story.title)}</h2>
              <div class="story-content">${formatStoryContent(introSection.story.content, lessonPlan.form.genre)}</div>
            </div>
          ` : ''}

          ${introSection.activities ? `
            <div class="section">
              <h2>Introduction Activities</h2>
              <div class="activities-content">${formatHtmlContent(introSection.activities)}</div>
            </div>
          ` : ''}

          ${introSection.storySpanish?.content ? `
            <div class="section">
              <h2>Introduction Story (Spanish): ${stripTitlePrefix(introSection.storySpanish.title)}</h2>
              <div class="story-content">${formatStoryContent(introSection.storySpanish.content, lessonPlan.form.genre)}</div>
            </div>
          ` : ''}

          ${introSection.activitiesSpanish ? `
            <div class="section">
              <h2>Introduction Activities (Spanish)</h2>
              <div class="activities-content">${formatHtmlContent(introSection.activitiesSpanish)}</div>
            </div>
          ` : ''}

          ${independentSection.story?.content ? `
            <div class="section">
              <h2>Independent Practice Story: ${stripTitlePrefix(independentSection.story.title)}</h2>
              <div class="story-content">${formatStoryContent(independentSection.story.content, lessonPlan.form.genre)}</div>
            </div>
          ` : ''}

          ${independentSection.activities ? `
            <div class="section">
              <h2>Independent Practice Activities</h2>
              <div class="activities-content">${formatHtmlContent(independentSection.activities)}</div>
            </div>
          ` : ''}

          ${independentSection.storySpanish?.content ? `
            <div class="section">
              <h2>Independent Practice Story (Spanish): ${stripTitlePrefix(independentSection.storySpanish.title)}</h2>
              <div class="story-content">${formatStoryContent(independentSection.storySpanish.content, lessonPlan.form.genre)}</div>
            </div>
          ` : ''}

          ${independentSection.activitiesSpanish ? `
            <div class="section">
              <h2>Independent Practice Activities (Spanish)</h2>
              <div class="activities-content">${formatHtmlContent(independentSection.activitiesSpanish)}</div>
            </div>
          ` : ''}
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();

    // Give some time for content to render before printing
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };
  };

  /**
   * Handles going back to saved lesson plans
   */
  const handleBackToSaved = () => {
    navigate('/small-group/saved');
  };

  /**
   * Handles generating translations for the lesson plan
   */
  const handleGenerateTranslation = async () => {
    if (!selectedTranslateLanguage) return;
    
    setIsTranslating(true);
    try {
      const introSection = lessonPlan.sections.introduction;
      const independentSection = lessonPlan.sections.independent;
      
      // Translate introduction section
      if (introSection.story?.content || introSection.activities) {
        const translatedIntro = await openaiService.translateSection(
          {
            storyContent: introSection.story?.content,
            activitiesContent: introSection.activities,
            targetLanguage: selectedTranslateLanguage,
            genre: lessonPlan.form.genre
          },
          {
            onStart: () => {},
            onProgress: (content) => {},
            onComplete: () => {}
          }
        );
        
        if (translatedIntro.story && introSection.story) {
          const translatedTitle = (translatedIntro as any).storyTitle || introSection.story.title;
          console.log('[Translation] Introduction story - using title:', translatedTitle, '(translated:', !!(translatedIntro as any).storyTitle, ')');
          handleSectionUpdate('introduction', {
            storySpanish: {
              ...introSection.story,
              id: `story-translated-${Date.now()}`,
              content: translatedIntro.story,
              title: translatedTitle
            },
            translationMetadata: {
              ...introSection.translationMetadata,
              storySpanish: createTranslationMetadata(
                introSection.story.content,
                selectedTranslateLanguage
              )
            }
          });
        }
        
        if (translatedIntro.activities) {
          handleSectionUpdate('introduction', {
            activitiesSpanish: translatedIntro.activities,
            translationMetadata: {
              ...introSection.translationMetadata,
              activitiesSpanish: createTranslationMetadata(
                introSection.activities,
                selectedTranslateLanguage
              )
            }
          });
        }
      }
      
      // Translate independent section
      if (independentSection.story?.content || independentSection.activities) {
        const translatedIndependent = await openaiService.translateSection(
          {
            storyContent: independentSection.story?.content,
            activitiesContent: independentSection.activities,
            targetLanguage: selectedTranslateLanguage,
            genre: lessonPlan.form.genre
          },
          {
            onStart: () => {},
            onProgress: (content) => {},
            onComplete: () => {}
          }
        );
        
        if (translatedIndependent.story && independentSection.story) {
          const translatedTitle = (translatedIndependent as any).storyTitle || independentSection.story.title;
          console.log('[Translation] Independent story - using title:', translatedTitle, '(translated:', !!(translatedIndependent as any).storyTitle, ')');
          handleSectionUpdate('independent', {
            storySpanish: {
              ...independentSection.story,
              id: `story-translated-${Date.now()}`,
              content: translatedIndependent.story,
              title: translatedTitle
            },
            translationMetadata: {
              ...independentSection.translationMetadata,
              storySpanish: createTranslationMetadata(
                independentSection.story.content,
                selectedTranslateLanguage
              )
            }
          });
        }
        
        if (translatedIndependent.activities) {
          handleSectionUpdate('independent', {
            activitiesSpanish: translatedIndependent.activities,
            translationMetadata: {
              ...independentSection.translationMetadata,
              activitiesSpanish: createTranslationMetadata(
                independentSection.activities,
                selectedTranslateLanguage
              )
            }
          });
        }
      }
      
      setSnackbar({
        open: true,
        message: `Translation to ${selectedTranslateLanguage} completed successfully!`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Translation error:', error);
      setSnackbar({
        open: true,
        message: 'Translation failed. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsTranslating(false);
    }
  };

  /**
   * Regenerates translations for a section after content modifications.
   * Will translate content if:
   * 1. There were previous translations, OR
   * 2. The lesson plan language is set to Spanish, OR  
   * 3. Explicit retranslation is requested via opts
   * Now includes translation metadata tracking for versioning and status
   */
  const regenerateSectionTranslations = async (
    section: 'introduction' | 'independent',
    opts?: { retranslateStory?: boolean; retranslateActivities?: boolean },
    contentOverrides?: { storyContent?: string; activitiesContent?: string }
  ) => {
    const targetLang = selectedTranslateLanguage;
    if (!targetLang) return;
    
    const sec = lessonPlan.sections[section];
    const hadStoryTranslation = !!sec.storySpanish?.content;
    const hadActivitiesTranslation = !!sec.activitiesSpanish;
    
    // Check if lesson plan is configured for Spanish translations
    const lessonNeedsSpanishTranslation = lessonPlan.form.language?.toLowerCase().includes('spanish') || 
                                         lessonPlan.form.language?.toLowerCase().includes('span') ||
                                         // Also check if there are ANY existing Spanish translations in the lesson plan
                                         !!(lessonPlan.sections.introduction.storySpanish?.content ||
                                            lessonPlan.sections.introduction.activitiesSpanish ||
                                            lessonPlan.sections.independent.storySpanish?.content ||
                                            lessonPlan.sections.independent.activitiesSpanish);
    
    // Determine what to translate based on:
    // 1. Explicit options (highest priority)
    // 2. Previous translations (maintain existing translations)
    // 3. Lesson language setting (auto-translate if Spanish is the target language)
    const shouldTranslateStory = opts?.retranslateStory ?? 
                                (hadStoryTranslation || (lessonNeedsSpanishTranslation && !!sec.story?.content));
    const shouldTranslateActivities = opts?.retranslateActivities ?? 
                                     (hadActivitiesTranslation || (lessonNeedsSpanishTranslation && !!sec.activities));
    
    if (!shouldTranslateStory && !shouldTranslateActivities) return;

    // Log the content being translated to verify it's the modified content
    const storyToTranslate = shouldTranslateStory
      ? (contentOverrides?.storyContent ?? sec.story?.content)
      : undefined;
    const activitiesToTranslate = shouldTranslateActivities
      ? (contentOverrides?.activitiesContent ?? sec.activities)
      : undefined;
    
    console.log(`🔄 Regenerating translations for ${section}:`, {
      story: shouldTranslateStory,
      activities: shouldTranslateActivities,
      language: targetLang,
      hadPreviousTranslations: { story: hadStoryTranslation, activities: hadActivitiesTranslation },
      lessonNeedsSpanish: lessonNeedsSpanishTranslation,
      storyLength: storyToTranslate?.length || 0,
      activitiesLength: activitiesToTranslate?.length || 0,
      storyPreview: storyToTranslate?.substring(0, 100) + '...',
      activitiesPreview: activitiesToTranslate?.substring(0, 100) + '...'
    });

    try {
      setIsTranslating(true);
      setTranslationsRegenerating(prev => ({ ...prev, [section]: true }));
      
      // Show info notification when auto-translating for the first time
      if (!hadStoryTranslation && !hadActivitiesTranslation && lessonNeedsSpanishTranslation) {
        setSnackbar({
          open: true,
          message: `Auto-translating ${section} content to Spanish...`,
          severity: 'info'
        });
      }
      
      // We'll translate story and activities in separate calls to eliminate cross-contamination
      const updates: any = {
        translationMetadata: sec.translationMetadata || {}
      };

      if (shouldTranslateStory && storyToTranslate) {
        const storyResult = await openaiService.translateSection(
          {
            storyContent: storyToTranslate,
            targetLanguage: targetLang,
            genre: lessonPlan.form.genre
          },
          { onStart: () => {}, onProgress: () => {}, onComplete: () => {} }
        );
        if (storyResult.story) {
          const baseStory = sec.story || { id: `story-${Date.now()}`, title: '', content: '' } as any;
          const translatedTitle = (storyResult as any).storyTitle || baseStory.title;
          console.log(`[Translation] ${section} story - using title:`, translatedTitle, '(translated:', !!(storyResult as any).storyTitle, ')');
          updates.storySpanish = {
            ...baseStory,
            id: `story-translated-${Date.now()}`,
            content: storyResult.story,
            title: translatedTitle
          };
          updates.translationMetadata.storySpanish = createTranslationMetadata(
            storyToTranslate,
            targetLang
          );
          console.log('✅ Story translation updated with metadata');
        }
      }

      if (shouldTranslateActivities && activitiesToTranslate) {
        const activitiesResult = await openaiService.translateSection(
          {
            activitiesContent: activitiesToTranslate,
            targetLanguage: targetLang,
            genre: lessonPlan.form.genre
          },
          { onStart: () => {}, onProgress: () => {}, onComplete: () => {} }
        );
        if (activitiesResult.activities) {
          updates.activitiesSpanish = activitiesResult.activities;
          updates.translationMetadata.activitiesSpanish = createTranslationMetadata(
            activitiesToTranslate,
            targetLang
          );
          console.log('✅ Activities translation updated with metadata');
        }
      }

      if (Object.keys(updates).length > 1) { // More than just translationMetadata
        handleSectionUpdate(section, updates);
        
        // Show success notification with more detail
        const sectionName = section === 'introduction' ? 'Introduction' : 'Independent Practice';
        const translatedItems = [];
        if (shouldTranslateStory && updates.storySpanish) translatedItems.push('story');
        if (shouldTranslateActivities && updates.activitiesSpanish) translatedItems.push('activities');
        
        setSnackbar({
          open: true,
          message: `${sectionName} ${translatedItems.join(' and ')} translated to Spanish`,
          severity: 'success'
        });
      }
    } catch (e) {
      console.error('Auto-translation error:', e);
      
      // Mark translations as error state
      const errorUpdates: any = {
        translationMetadata: sec.translationMetadata || {}
      };
      
      if (shouldTranslateStory && sec.translationMetadata?.storySpanish) {
        errorUpdates.translationMetadata.storySpanish = {
          ...sec.translationMetadata.storySpanish,
          status: 'error' as TranslationStatus
        };
      }
      
      if (shouldTranslateActivities && sec.translationMetadata?.activitiesSpanish) {
        errorUpdates.translationMetadata.activitiesSpanish = {
          ...sec.translationMetadata.activitiesSpanish,
          status: 'error' as TranslationStatus
        };
      }
      
      handleSectionUpdate(section, errorUpdates);
      
      setSnackbar({
        open: true,
        message: 'Failed to update translations after modification.',
        severity: 'error'
      });
    } finally {
      setIsTranslating(false);
      setTranslationsRegenerating(prev => ({ ...prev, [section]: false }));
    }
  };

  /**
   * Handles saving the lesson plan to the database
   * Saves the current lesson plan with all form data and generated content
   */
  const handleSave = async () => {
    if (!validateForm()) return;

    if (!hasDownloadableContent()) {
      setSnackbar({
        open: true,
        message: 'No content available to save. Please generate lesson content first.',
        severity: 'warning'
      });
      return;
    }

    try {
      setLoading(true);
      
      // Prepare the lesson plan data for saving
      const lessonPlanData = {
        _id: isEditingMode ? originalLessonPlanId : null, // Use original ID when editing
        groups: [], // TODO: Add group selection functionality if needed
        form: {
          ...lessonPlan.form,
          subject: selectedSubject // Always include current subject
        },
        standard: selectedStandard ? {
          code: selectedStandard.code || selectedStandard.standard,
          description: selectedStandard.description,
          _id: selectedStandard._id,
          strand: selectedStandard.strand // Include strand for Math
        } : null,
        sections: lessonPlan.sections
      };

      const response = await apiAxiosInstance.post('/api/small-group-lesson-plans', lessonPlanData);
      
      // Update the lesson plan ID if it was a new save
      if (!isEditingMode && response.data.lessonPlanId) {
        // CRITICAL FIX: Switch to editing mode after first save to prevent duplicates
        setIsEditingMode(true);
        setOriginalLessonPlanId(response.data.lessonPlanId);
        
        setLessonPlan(prev => ({
          ...prev,
          id: response.data.lessonPlanId,
          metadata: {
            ...prev.metadata,
            createdAt: new Date(),
            updatedAt: new Date()
          }
        }));
      } else if (isEditingMode) {
        // Update metadata for existing lesson plan
        setLessonPlan(prev => ({
          ...prev,
          metadata: {
            ...prev.metadata,
            updatedAt: new Date()
          }
        }));
      }

      setSnackbar({
        open: true,
        message: isEditingMode && originalLessonPlanId ? 'Lesson plan updated successfully!' : 'Lesson plan saved successfully!',
        severity: 'success'
      });

    } catch (error) {
      console.error('Error saving lesson plan:', error);
      setSnackbar({
        open: true,
        message: isEditingMode && originalLessonPlanId ? 'Failed to update lesson plan. Please try again.' : 'Failed to save lesson plan. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Checks if there's any content or unsaved changes that would be lost
   * @returns boolean indicating if there's content to lose
   */
  const hasUnsavedContent = (): boolean => {
    const hasStoryContent = !!(lessonPlan.sections.introduction.story?.content || 
                              lessonPlan.sections.independent.story?.content);
    const hasActivitiesContent = !!(lessonPlan.sections.introduction.activities || 
                                   lessonPlan.sections.independent.activities);
    const hasChatHistory = chatHistory.length > 0;
    
    // Only consider it unsaved if there's actual generated content or chat history
    // Don't consider form settings (topic, standard) as unsaved content for new lesson plan
    return hasStoryContent || hasActivitiesContent || hasChatHistory;
  };

  /**
   * Resets the lesson plan to initial state for starting a new lesson
   */
  const handleNewLessonPlan = () => {
    if (hasUnsavedContent()) {
      setConfirmNewLessonOpen(true);
    } else {
      resetLessonPlan();
    }
  };

  /**
   * Resets all state to initial values
   */
  const resetLessonPlan = () => {
    // Reset lesson plan to initial state
    setLessonPlan(initialLessonPlan);
    
    // Reset form-related state
    setSelectedStandard(null);
    setActiveTab('introduction');
    setParametersExpanded(true);
    
    // Reset section-specific Lexile levels
    setSectionLexileLevels({
      introduction: '',
      independent: '',
      baseLexileLevel: ''
    });
    
    // Clear chat state
    setChatMessage('');
    setChatHistory([]);
    setIsChatProcessing(false);
    
    // Reset UI state
    setError('');
    setDownloadAnchorEl(null);
    
    // Reset editing mode
    setIsEditingMode(false);
    setOriginalLessonPlanId(null);
    
    // Cancel any ongoing generation
    if (cancelGeneration) {
      cancelGeneration();
    }
    
    // Close confirmation dialog
    setConfirmNewLessonOpen(false);
    
    // Show success message
    setSnackbar({
      open: true,
      message: 'New lesson plan started! All content has been cleared and you can begin fresh.',
      severity: 'success'
    });
  };

  /**
   * Handles the confirmation dialog for starting a new lesson
   */
  const handleConfirmNewLesson = () => {
    resetLessonPlan();
  };

  /**
   * Handles canceling the new lesson confirmation
   */
  const handleCancelNewLesson = () => {
    setConfirmNewLessonOpen(false);
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        {/* Editing mode info row */}
        {isEditingMode && (
          <Box display="flex" alignItems="center" gap={1} mb={1.5}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBackToSaved}
              variant="outlined"
              size="small"
            >
              Back to Saved Plans
            </Button>
            <Chip 
              label="EDITING MODE" 
              color="warning" 
              variant="filled" 
              size="small"
              sx={{ fontWeight: 600 }}
            />
            <Typography variant="body2" color="text.secondary">
              You are editing: {lessonPlan.form.topic || 'Untitled Lesson'}
            </Typography>
          </Box>
        )}
        
        {/* Main header row with title and action buttons */}
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box flex={1} />
          
          {/* Centered title */}
          <Typography 
            variant="h5" 
            component="h1" 
            sx={{ 
              fontWeight: 700, 
              background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textAlign: 'center',
              flex: 1
            }}
          >
            Lesson Plan Generator
          </Typography>
          
          {/* Action buttons on the right */}
          <Box display="flex" gap={1.5} flex={1} justifyContent="flex-end">
            <Button
              startIcon={<AddIcon />}
              onClick={handleNewLessonPlan}
              variant="contained"
              size="small"
              sx={{
                background: 'linear-gradient(45deg, #4caf50 30%, #66bb6a 90%)',
                color: 'white',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(45deg, #388e3c 30%, #4caf50 90%)',
                }
              }}
            >
              New Lesson Plan
            </Button>
            <VideoHelpButton 
              url="https://www.youtube.com/watch?v=hNBSAx4V5Gk" 
              title="Using the Lesson Plan Generator"
              buttonLabel="Watch tutorial"
              color="secondary"
              emphasis="solid"
            />
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Form Section */}
        <Grid item xs={12} lg={4}>
          <Paper elevation={1} sx={{ p: 2, position: 'sticky', top: 16 }}>
            {/* Enhanced Collapsible Parameters Header */}
            <Box 
              display="flex" 
              alignItems="center" 
              justifyContent="space-between" 
              sx={{ 
                cursor: 'pointer', 
                mb: 2,
                p: 2,
                borderRadius: 2,
                backgroundColor: parametersExpanded ? 'transparent' : 'primary.main',
                color: parametersExpanded ? 'text.primary' : 'white',
                border: parametersExpanded ? '2px solid transparent' : '2px solid #1976d2',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  backgroundColor: parametersExpanded ? 'rgba(25, 118, 210, 0.04)' : 'primary.dark',
                  transform: 'translateY(-1px)',
                  boxShadow: parametersExpanded ? 2 : 4
                },
                '&:active': {
                  transform: 'translateY(0px)',
                }
              }}
              onClick={() => setParametersExpanded(!parametersExpanded)}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 600,
                  color: 'inherit'
                }}>
                  📝 Lesson Parameters
                </Typography>
                {!parametersExpanded && (
                  <Chip 
                    size="small" 
                    label="Click to expand" 
                    variant="filled"
                    sx={{ 
                      color: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      borderColor: 'rgba(255, 255, 255, 0.4)',
                      fontSize: '0.75rem',
                      height: 24,
                      fontWeight: 500,
                      '& .MuiChip-label': {
                        px: 1.5
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                        borderColor: 'rgba(255, 255, 255, 0.6)',
                      },
                      transition: 'all 0.2s ease'
                    }}
                  />
                )}
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                {/* Topics removed from collapsed view */}
                <IconButton 
                  size="small" 
                  sx={{ 
                    color: 'inherit',
                    backgroundColor: parametersExpanded ? 'transparent' : 'rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      backgroundColor: parametersExpanded ? 'rgba(25, 118, 210, 0.1)' : 'rgba(255, 255, 255, 0.2)',
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  {parametersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
            </Box>
            
            {/* Collapsible Parameters Content */}
            <Collapse 
              in={parametersExpanded}
              timeout={300}
              unmountOnExit={false}
            >
              {/* Compact Form Layout */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Subject Selection Section */}
                <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                  <Typography variant="subtitle2" sx={{ 
                    mb: 1.5, 
                    fontWeight: 'bold',
                    color: '#2c5282',
                    fontSize: '0.875rem'
                  }}>
                    Subject
                  </Typography>
                  <TextField
                    select
                    label="Select Subject"
                    value={selectedSubject}
                    onChange={(e) => {
                      const newSubject = e.target.value;
                      setSelectedSubject(newSubject);
                      // CRITICAL: Sync to form data so backend receives it
                      handleFormUpdate('subject', newSubject);
                    }}
                    required
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{
                      displayEmpty: true
                    }}
                  >
                    <MenuItem value="ELA">English Language Arts (ELA)</MenuItem>
                    <MenuItem value="Math">Mathematics</MenuItem>
                  </TextField>
                </Paper>

                {/* Learning Standard Section */}
                <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                  <Typography variant="subtitle2" sx={{ 
                    mb: 1.5, 
                    fontWeight: 'bold',
                    color: '#2c5282',
                    fontSize: '0.875rem'
                  }}>
                    Learning Standard
                  </Typography>
                  <Autocomplete
                    options={filteredStandards}
                    getOptionLabel={(option) => {
                      if (!option) return '';
                      const code = option.standard || option.code || 'N/A';
                      const desc = option.description || option.short_description || 'Standard';
                      return `${code} - ${capitalizeFirstLetter(desc)}`;
                    }}
                    isOptionEqualToValue={(option, value) => {
                      if (!option || !value) return false;
                      return option._id === value._id;
                    }}
                    value={selectedStandard}
                    onChange={(event, newValue) => {
                      setSelectedStandard(newValue);
                      handleFormUpdate('standardId', newValue ? newValue._id : '');
                      
                      // For Math, auto-populate content strand
                      if (selectedSubject === 'Math' && newValue) {
                        const strand = extractStrandFromStandard(newValue);
                        handleFormUpdate('contentType', strand);
                        if (strand) {
                          handleFormUpdate('genre', strand); // Also set legacy field
                        }
                      }
                      
                      // Auto-select difficulty level based on grade level
                      if (newValue?.gradeLevel) {
                        if (selectedSubject === 'ELA') {
                          const recommendedLevel = getRecommendedLexileLevel(newValue.gradeLevel);
                          if (recommendedLevel && !lessonPlan.form.difficulty && !lessonPlan.form.lexileLevel) {
                            handleFormUpdate('difficulty', recommendedLevel);
                            handleFormUpdate('lexileLevel', recommendedLevel);
                          }
                        }
                      }
                    }}
                    loading={loading}
                    fullWidth
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        error={!!error}
                        helperText={error}
                        placeholder="Search for a standard..."
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Paper>

                {/* Content Settings Section */}
                <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                  <Typography variant="subtitle2" sx={{ 
                    mb: 1.5, 
                    fontWeight: 'bold',
                    color: '#2c5282',
                    fontSize: '0.875rem'
                  }}>
                    Content Settings
                  </Typography>
                  
                  {/* Reading Level / Problem Difficulty and Genre / Content Strand Row */}
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 2,
                    mb: 2
                  }}>
                    <TextField
                      select
                      label={getFieldLabels(selectedSubject).difficulty}
                      value={lessonPlan.form.difficulty || lessonPlan.form.lexileLevel || ''}
                      onChange={(e) => {
                        handleFormUpdate('difficulty', e.target.value);
                        // Also update legacy field for backward compatibility
                        if (selectedSubject === 'ELA') {
                          handleFormUpdate('lexileLevel', e.target.value);
                        }
                      }}
                      required
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      SelectProps={{
                        displayEmpty: true
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>Select {selectedSubject === 'Math' ? 'difficulty' : 'reading level'}...</em>
                      </MenuItem>
                      {selectedSubject === 'ELA' ? (
                        lexileLevels.map((level) => (
                          <MenuItem key={level.level} value={level.level}>
                            {level.level}
                          </MenuItem>
                        ))
                      ) : (
                        getMathDifficultyLevels(teacher?.gradeLevel).map((level) => (
                          <MenuItem key={level.value} value={level.value}>
                            {level.label}
                          </MenuItem>
                        ))
                      )}
                    </TextField>

                    <TextField
                      select
                      label={getFieldLabels(selectedSubject).contentType}
                      value={selectedSubject === 'ELA' 
                        ? (lessonPlan.form.genre || lessonPlan.form.contentType || '') 
                        : (lessonPlan.form.contentType || '')}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        console.log('Genre selection changed:', { newValue, previousGenre: lessonPlan.form.genre, previousContentType: lessonPlan.form.contentType });
                        // Update both fields for ELA
                        if (selectedSubject === 'ELA') {
                          setLessonPlan(prev => ({
                            ...prev,
                            form: {
                              ...prev.form,
                              genre: newValue,
                              contentType: newValue
                            }
                          }));
                        } else {
                          handleFormUpdate('contentType', newValue);
                        }
                      }}
                      required
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      SelectProps={{
                        displayEmpty: true,
                        renderValue: (value) => {
                          if (!value) {
                            return <em style={{ color: '#999' }}>Select {selectedSubject === 'Math' ? 'strand' : 'genre'}...</em>;
                          }
                          return value as string;
                        }
                      }}
                      disabled={selectedSubject === 'Math'} // Auto-populated for Math
                    >
                      {selectedSubject === 'ELA' ? [
                        <MenuItem key="empty-genre" value="" disabled>
                          <em>Select genre...</em>
                        </MenuItem>,
                        ...genres.map((genre) => (
                          <MenuItem key={genre.value} value={genre.value}>
                            {genre.value}
                          </MenuItem>
                        ))
                      ] : (
                        <MenuItem key="math-strand" value={extractStrandFromStandard(selectedStandard)}>
                          {extractStrandFromStandard(selectedStandard) || 'Select a standard first'}
                        </MenuItem>
                      )}
                    </TextField>
                  </Box>

                  {/* Topics Row - Only for ELA */}
                  {selectedSubject === 'ELA' && (
                    <Box sx={{ 
                      display: 'grid', 
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 2,
                      mb: 2
                    }}>
                      <TextField
                        label="Introduction Topic"
                        value={lessonPlan.form.topic}
                        onChange={(e) => handleFormUpdate('topic', e.target.value)}
                        placeholder='e.g., "friendship", "ocean animals" (or leave blank)'
                        fullWidth
                        size="small"
                        helperText="Leave blank for random topic"
                      />

                      <TextField
                        label="Independent Practice Topic"
                        value={lessonPlan.form.independentPracticeTopic}
                        onChange={(e) => handleFormUpdate('independentPracticeTopic', e.target.value)}
                        placeholder='e.g., "teamwork", "weather patterns" (or leave blank)'
                        fullWidth
                        size="small"
                        helperText="Leave blank for random topic"
                      />
                    </Box>
                  )}

                  {/* Language, Translation, and Text Length Row */}
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 2,
                    mb: 2
                  }}>
                    <TextField
                      select
                      label="Language"
                      value={lessonPlan.form.language}
                      onChange={(e) => handleFormUpdate('language', e.target.value)}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      SelectProps={{ displayEmpty: true }}
                      helperText="Default language for Story and Activities"
                    >
                      {languages.map((lang) => (
                        <MenuItem key={lang.value} value={lang.value}>{lang.label}</MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      type="number"
                      label={getFieldLabels(selectedSubject).contentLength}
                      value={lessonPlan.form.contentLength || lessonPlan.form.paragraphCount || 3}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 3;
                        handleFormUpdate('contentLength', value);
                        if (selectedSubject === 'ELA') {
                          handleFormUpdate('paragraphCount', value);
                        }
                      }}
                      inputProps={{
                        min: selectedSubject === 'Math' ? 3 : 1,
                        max: selectedSubject === 'Math' ? 15 : 10
                      }}
                      fullWidth
                      size="small"
                      helperText={selectedSubject === 'Math' ? '3-15 problems' : '1-10 paragraphs'}
                      sx={{ maxWidth: { xs: '100%', sm: 200 } }}
                    />
                  </Box>


                  {/* Writing (Revising and Editing) Option - Only for ELA */}
                  {selectedSubject === 'ELA' && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={lessonPlan.form.revisingAndEditing}
                          onChange={(e) => handleFormUpdate('revisingAndEditing', e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Writing (Revising and Editing)
                          </Typography>
                          <Tooltip title="Creates STAAR-style passages with numbered sentences for revising and editing practice. Lesson activities will focus on identifying and correcting errors in grammar, punctuation, and sentence structure.">
                            <InfoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          </Tooltip>
                        </Box>
                      }
                      sx={{ 
                        mt: 1,
                        alignItems: 'flex-start',
                        '& .MuiFormControlLabel-label': {
                          fontSize: '0.9rem',
                          lineHeight: 1.2
                        }
                      }}
                    />
                  )}
                </Paper>

                                 {/* Generate Button */}
                 <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                   <Button
                     fullWidth
                     variant="contained"
                     size="large"
                     startIcon={<AutoAwesomeIcon />}
                     onClick={handleGenerate}
                     disabled={isGenerating}
                     className="action-button-generate"
                     sx={{
                       py: 1.5,
                       fontSize: '1rem',
                       fontWeight: 600,
                     }}
                   >
                     {isGenerating ? 'Generating...' : 'Generate Lesson Plan'}
                   </Button>
                 </Box>
                   
              </Box>
            </Collapse>

            {/* Cancel Generation Button - Visible outside collapse for easy access */}
                   {isGenerating && (
              <Box sx={{ mt: 2, mb: 1 }}>
                     <Button
                       fullWidth
                       variant="outlined"
                  color="error"
                  size="medium"
                       onClick={cancelGeneration}
                  sx={{
                    py: 1,
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2,
                      backgroundColor: 'error.light',
                      color: 'white'
                    }
                  }}
                >
                  🛑 Cancel Generation
                     </Button>
                 </Box>
            )}

            {/* Chat Interface */}
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ opacity: hasAllContentGenerated ? 1 : 0.6 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <ChatIcon color={hasAllContentGenerated ? "primary" : "disabled"} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: hasAllContentGenerated ? 'inherit' : 'text.disabled' }}>
                  Content Modifier
                </Typography>
                <Tooltip 
                  title="Use AI to modify your lesson content. You can ask to change the story, activities, difficulty level, or add specific content. Works with both Introduction and Independent Practice sections."
                  placement="top"
                  arrow
                >
                  <IconButton size="small" sx={{ ml: 0.5 }}>
                    <InfoIcon fontSize="small" color={hasAllContentGenerated ? "action" : "disabled"} />
                  </IconButton>
                </Tooltip>
              </Box>
              
              {/* Subtitle explaining what can be modified */}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.875rem' }}>
                Modify the <strong style={{ 
                  color: '#1976d2', 
                  backgroundColor: '#e3f2fd', 
                  padding: '2px 6px', 
                  borderRadius: '4px',
                  border: '1px solid #90caf9'
                }}>{activeTab === 'introduction' ? 'Introduction' : 'Independent Practice'}</strong> section.
              </Typography>
              
              {!hasAllContentGenerated && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                  ⏳ Generate content for both Introduction and Independent Practice sections to enable the Content Modifier.
                </Typography>
              )}

              {/* Chat History - Simplified to show only user requests */}
              <Paper 
                ref={chatHistoryRef}
                variant="outlined" 
                sx={{ 
                  height: 160, 
                  overflow: 'auto', 
                  mb: 1.5,
                  backgroundColor: hasAllContentGenerated ? 'grey.50' : 'grey.100',
                  scrollBehavior: 'smooth'
                }}
              >
                <List dense>
                  {chatHistory.filter(msg => msg.type === 'user').length === 0 ? (
                    <ListItem>
                      <ListItemText 
                        primary={hasAllContentGenerated ? "No requests yet" : "Content Modifier disabled"}
                        secondary={hasAllContentGenerated ? "Send a request to modify your lesson content" : "Generate all content first to enable modifications"}
                        sx={{ textAlign: 'center', color: 'text.secondary' }}
                      />
                    </ListItem>
                  ) : (
                    chatHistory
                      .filter(msg => msg.type === 'user')
                      .map((msg) => (
                        <ListItem key={msg.id}>
                          <ListItemText
                            primary={msg.message}
                            secondary={msg.timestamp.toLocaleTimeString()}
                            sx={{
                              '& .MuiListItemText-primary': {
                                color: 'primary.main',
                                fontWeight: 600
                              }
                            }}
                          />
                        </ListItem>
                      ))
                  )}
                  {isChatProcessing && (
                    <ListItem>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <CircularProgress size={16} />
                            <span>Processing...</span>
                          </Box>
                        }
                      />
                    </ListItem>
                  )}
                </List>
              </Paper>

              {/* Chat Input with Enhanced Visual Feedback */}
              <TextField
                fullWidth
                multiline
                rows={2}
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                placeholder={
                  !hasAllContentGenerated 
                    ? "Generate all content first to enable Content Modifier..."
                    : isChatProcessing
                    ? "Processing your modification request..."
                    : `e.g., '${placeholderExamples[placeholderIndex]}'`
                }
                variant="outlined"
                size="small"
                disabled={!hasAllContentGenerated || isChatProcessing}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && hasAllContentGenerated && !isChatProcessing) {
                    e.preventDefault();
                    handleChatSubmit();
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {isChatProcessing ? (
                        <CircularProgress size={20} />
                      ) : (
                      <IconButton
                        onClick={handleChatSubmit}
                        disabled={!hasAllContentGenerated || !chatMessage.trim() || isChatProcessing}
                        size="small"
                          color={chatMessage.trim() ? "primary" : "default"}
                      >
                        <SendIcon />
                      </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isChatProcessing ? 'rgba(25, 118, 210, 0.05)' : 'inherit',
                    borderColor: isChatProcessing ? 'primary.main' : 'inherit'
                  }
                }}
              />
              
              {/* Processing Status Indicator with Stop Button */}
              {isChatProcessing && (
                <Box 
                  display="flex" 
                  alignItems="center" 
                  justifyContent="space-between"
                  sx={{ 
                    mt: 1, 
                    p: 1,
                    backgroundColor: 'rgba(25, 118, 210, 0.05)',
                    borderRadius: 1,
                    border: '1px solid rgba(25, 118, 210, 0.2)'
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500 }}>
                      🤖 AI is modifying your {activeTab} content...
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={handleCancelChat}
                    sx={{ 
                      minWidth: 'auto',
                      px: 1,
                      py: 0.5,
                      fontSize: '0.75rem'
                    }}
                  >
                    Stop
                  </Button>
                </Box>
              )}
              
              {/* Streaming now handled directly in sections like during generation */}
            </Box>

            {/* Translation Section with Status Indicators */}
            {(hasDownloadableContent() || hasIntroductionContent || hasIndependentContent) && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
                    🌐 Translation
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Translate your lesson plan to another language
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                    <TextField
                      select
                      label="Translate to"
                      value={selectedTranslateLanguage}
                      onChange={(e) => setSelectedTranslateLanguage(e.target.value)}
                      size="small"
                      sx={{ minWidth: 150 }}
                      InputLabelProps={{ shrink: true }}
                    >
                      {filteredTranslateOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </TextField>

                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={isTranslating ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
                      onClick={handleGenerateTranslation}
                      disabled={!selectedTranslateLanguage || isTranslating || areActionButtonsDisabled}
                      sx={{
                        py: 1,
                        px: 3,
                        fontSize: '0.85rem',
                        fontWeight: 600,
                      }}
                    >
                      {isTranslating ? 'Translating...' : 'Generate Translation'}
                    </Button>
                  </Box>
                  
                </Box>

                <Divider sx={{ my: 2 }} />
              </>
            )}

            {/* Download Section - Moved outside collapsible panel for better visibility */}
            {(hasDownloadableContent() || hasIntroductionContent || hasIndependentContent) && (
              <>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
                    💾 Save, Export & Print
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Save your lesson plan to the database, export as files, or print directly
                  </Typography>
                  
                  <Grid container spacing={1.5}>
                    <Grid item xs={4}>
                      <Button
                        fullWidth
                        variant="contained"
                        size="medium"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={areActionButtonsDisabled}
                        className="action-button-save"
                        sx={{
                          py: 1.2,
                          fontSize: '0.85rem',
                          fontWeight: 600,
                        }}
                      >
                        {loading ? 'Saving...' : areActionButtonsDisabled ? 'Processing...' : (isEditingMode && originalLessonPlanId) ? 'Update' : 'Save'}
                      </Button>
                    </Grid>
                    
                    <Grid item xs={4}>
                      <Button
                        fullWidth
                        variant="contained"
                        size="medium"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadClick}
                        disabled={areActionButtonsDisabled}
                        className="action-button-download"
                        sx={{
                          py: 1.2,
                          fontSize: '0.85rem',
                          fontWeight: 600,
                        }}
                      >
                        Download
                      </Button>
                    </Grid>
                    
                    <Grid item xs={4}>
                      <Button
                        fullWidth
                        variant="contained"
                        size="medium"
                        startIcon={<PrintIcon />}
                        onClick={handlePrint}
                        disabled={areActionButtonsDisabled}
                        sx={{
                          py: 1.2,
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                          boxShadow: 2,
                          '&:hover': {
                            boxShadow: 4,
                            background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                          },
                          '&:disabled': {
                            background: 'rgba(0, 0, 0, 0.12)',
                            color: 'rgba(0, 0, 0, 0.26)',
                          }
                        }}
                      >
                        Print
                      </Button>
                    </Grid>
                  </Grid>
                  
                  {/* Download Menu */}
                  <Menu
                    anchorEl={downloadAnchorEl}
                    open={Boolean(downloadAnchorEl) && !areActionButtonsDisabled}
                    onClose={handleDownloadClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'center',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'center',
                    }}
                  >
                    <MuiMenuItem onClick={handleDownloadPDF} disabled={areActionButtonsDisabled}>
                      <PictureAsPdfIcon sx={{ mr: 1, color: 'error.main' }} />
                      <Box>
                        <Typography variant="body2" fontWeight={600}>PDF</Typography>
                        <Typography variant="caption" color="text.secondary">Best for printing</Typography>
                      </Box>
                    </MuiMenuItem>
                    <MuiMenuItem onClick={handleDownloadWord} disabled={areActionButtonsDisabled}>
                      <DescriptionIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Box>
                        <Typography variant="body2" fontWeight={600}>Word</Typography>
                        <Typography variant="caption" color="text.secondary">Editable document</Typography>
                      </Box>
                    </MuiMenuItem>
                  </Menu>
                </Box>
              </>
            )}
          </Paper>
        </Grid>

        {/* Content Section */}
        <Grid item xs={12} lg={8}>
          {/* Progress indicator */}
          {isGenerating && (
            <Alert 
              severity="info" 
              sx={{ mb: 3 }}
            >
              <Typography variant="body2">
                🤖 AI is generating your lesson plan... This may take 1-2 minutes.
              </Typography>
            </Alert>
          )}

          {/* Section Tabs */}
          <SectionTabs
            activeTab={activeTab === 'introduction' ? 0 : 1}
            onTabChange={(tab: number) => setActiveTab(tab === 0 ? 'introduction' : 'independent')}
            sections={lessonPlan.sections}
            disabled={isGenerating}
            streamingStates={streamingStates}
          />

          {/* Section Content */}
          <Box mt={2}>
            {activeTab === 'introduction' ? (
              <Paper 
                elevation={1} 
                sx={{ 
                  p: 2, 
                  border: '2px solid',
                  borderColor: 'primary.light',
                  backgroundColor: 'primary.50',
                  position: 'relative'
                }}
              >
                <Chip 
                  label="Editable Section" 
                  size="small" 
                  color="primary" 
                  sx={{ 
                    position: 'absolute', 
                    top: -10, 
                    left: 16, 
                    zIndex: 1,
                    fontSize: '0.75rem',
                    fontWeight: 600
                  }} 
                />
                <IntroductionSection
                  section={lessonPlan.sections.introduction}
                  isStreaming={{
                    story: streamingStates['intro-story']?.isActive || (isChatProcessing && activeTab === 'introduction'),
                    activities: streamingStates['intro-activities']?.isActive || (isChatProcessing && activeTab === 'introduction'),
                  }}
                  streamingStates={streamingStates}
                  streamingContent={streamingContent}
                  genre={lessonPlan.form.genre}
                  subject={selectedSubject}
                  translationRegenerating={translationsRegenerating.introduction}
                  onRegenerate={(type: 'story' | 'activities') => {
                    regenerateContent(lessonPlan, 'introduction', type);
                  }}
                  onEdit={(content: string, type: 'story' | 'activities') => {
                    if (type === 'story') {
                      handleStoryUpdate('introduction', content);
                    } else {
                      handleActivitiesUpdate('introduction', content);
                    }
                  }}
                />
              </Paper>
            ) : (
              <Paper 
                elevation={1} 
                sx={{ 
                  p: 2, 
                  border: '2px solid',
                  borderColor: 'primary.light',
                  backgroundColor: 'primary.50',
                  position: 'relative'
                }}
              >
                <Chip 
                  label="Editable Section" 
                  size="small" 
                  color="primary" 
                  sx={{ 
                    position: 'absolute', 
                    top: -10, 
                    left: 16, 
                    zIndex: 1,
                    fontSize: '0.75rem',
                    fontWeight: 600
                  }} 
                />
                <IndependentPracticeSection
                  section={lessonPlan.sections.independent}
                  isStreaming={{
                    story: streamingStates['practice-story']?.isActive || (isChatProcessing && activeTab === 'independent'),
                    activities: streamingStates['practice-activities']?.isActive || (isChatProcessing && activeTab === 'independent'),
                  }}
                  streamingStates={streamingStates}
                  streamingContent={streamingContent}
                  genre={lessonPlan.form.genre}
                  subject={selectedSubject}
                  translationRegenerating={translationsRegenerating.independent}
                  onRegenerate={(type: 'story' | 'activities') => {
                    regenerateContent(lessonPlan, 'independent', type);
                  }}
                  onEdit={(content: string, type: 'story' | 'activities') => {
                    if (type === 'story') {
                      handleStoryUpdate('independent', content);
                    } else {
                      handleActivitiesUpdate('independent', content);
                    }
                  }}
                />
              </Paper>
            )}

          </Box>
        </Grid>
      </Grid>


      {/* Confirmation Dialog for New Lesson Plan */}
      <Dialog
        open={confirmNewLessonOpen}
        onClose={handleCancelNewLesson}
        aria-labelledby="confirm-new-lesson-dialog-title"
        aria-describedby="confirm-new-lesson-dialog-description"
      >
        <DialogTitle id="confirm-new-lesson-dialog-title">
          Start New Lesson Plan?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-new-lesson-dialog-description">
            You have generated content in your current lesson plan. Starting a new lesson plan will clear all current content and start fresh. The following will be cleared:
          </DialogContentText>
          <List dense sx={{ mt: 1 }}>
            {(lessonPlan.sections.introduction.story?.content || lessonPlan.sections.independent.story?.content) && (
              <ListItem>
                <ListItemText primary="• Generated story content" />
              </ListItem>
            )}
            {(lessonPlan.sections.introduction.activities || lessonPlan.sections.independent.activities) && (
              <ListItem>
                <ListItemText primary="• Generated activities" />
              </ListItem>
            )}
            {chatHistory.length > 0 && (
              <ListItem>
                <ListItemText primary="• Chat history and modifications" />
              </ListItem>
            )}
          </List>
          <DialogContentText sx={{ mt: 2 }}>
            This will start a completely fresh lesson plan. Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelNewLesson} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmNewLesson} color="error" variant="contained">
            Start New Lesson Plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for Generate Lesson Plan */}
      <Dialog
        open={confirmGenerateOpen}
        onClose={() => setConfirmGenerateOpen(false)}
        aria-labelledby="confirm-generate-dialog-title"
        aria-describedby="confirm-generate-dialog-description"
      >
        <DialogTitle id="confirm-generate-dialog-title">
          Replace Current Lesson Plan?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-generate-dialog-description">
            You already have content in your current lesson plan. Generating a new lesson plan will completely replace all existing content including:
          </DialogContentText>
          <List dense sx={{ mt: 1 }}>
            {lessonPlan.sections.introduction.story?.content && (
              <ListItem>
                <ListItemText primary="• Introduction story" />
              </ListItem>
            )}
            {lessonPlan.sections.introduction.activities && (
              <ListItem>
                <ListItemText primary="• Introduction activities" />
              </ListItem>
            )}
            {lessonPlan.sections.independent.story?.content && (
              <ListItem>
                <ListItemText primary="• Independent practice story" />
              </ListItem>
            )}
            {lessonPlan.sections.independent.activities && (
              <ListItem>
                <ListItemText primary="• Independent practice activities" />
              </ListItem>
            )}
            {chatHistory.length > 0 && (
              <ListItem>
                <ListItemText primary="• Chat history and modifications" />
              </ListItem>
            )}
          </List>
          <DialogContentText sx={{ mt: 2 }}>
            Are you sure you want to proceed? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmGenerateOpen(false)} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={() => {
              setConfirmGenerateOpen(false);
              proceedWithGeneration();
            }} 
            color="error" 
            variant="contained"
          >
            Generate New Lesson Plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SmallGroupLessonPlan; 